import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, Plus, Pencil, Trash2, Package, ShoppingBag, Sparkles, X, Upload, Images, Image as ImageIcon, KeyRound, DatabaseBackup, Video, Rows3, Activity, Users, ChevronDown, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BRAND, CATEGORIES, formatINR, INDIAN_STATES, ADMIN_PHONES, isAdminPhone } from "@/config";
import { useCustomer } from "@/context/CustomerContext";
import { CrmCartDetails } from "@/components/admin/CrmCartDetails";
import { SizeOptionsField } from "@/components/admin/SizeOptionsField";
import { WhatsAppCampaigns } from "@/components/admin/WhatsAppCampaigns";
import { WhatsAppInbox } from "@/components/admin/WhatsAppInbox";
import { WhatsAppProfile } from "@/components/admin/WhatsAppProfile";
import { DiscountCodes } from "@/components/admin/DiscountCodes";
import { CustomerGroupsBar, CustomerGroupPicker, useCustomerGroups } from "@/components/admin/CustomerGroups";
import { ItemDetailsSummary } from "@/components/store/ItemDetailsSummary";
import { itemDetailsText } from "@/lib/productDetails";

const EMPTY_FORM = {
  name: "", sku: "", category: "Mugs & Drinkware", price: "", bulk_price: "", bulk_min: "",
  description: "", image: "", images: [], videos: [], substrate: "", press_temp: "", press_time: "", press_pressure: "",
  stock: "", featured: false, is_live: true, is_new_launch: false, size_options: [],
};

function GalleryField({ images, onChange, inputCls }) {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const inputRef = useRef(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/upload-images", fd);
      onChange([...images, ...data.urls]);
      toast.success(`${data.urls.length} photo${data.urls.length === 1 ? "" : "s"} added`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const addUrl = () => {
    if (!url.trim()) return;
    onChange([...images, url.trim()]);
    setUrl("");
  };

  return (
    <div className="space-y-3 sm:col-span-2" data-testid="product-form-gallery">
      <div className="flex flex-wrap gap-3">
        {images.map((src, i) => (
          <div key={src + i} data-testid={`product-form-image-${i}`} className="relative h-20 w-20 overflow-hidden rounded-xl border border-white/10 bg-[#0A0B0E]/60">
            <img src={src} alt="" className="h-full w-full object-cover" />
            {i === 0 ? (
              <span className="absolute inset-x-0 bottom-0 bg-cyan-400/90 text-center font-mono text-[8px] uppercase tracking-wider text-[#0A0B0E]">Cover</span>
            ) : (
              <button
                type="button"
                data-testid={`product-form-make-cover-${i}`}
                onClick={() => onChange([src, ...images.filter((_, j) => j !== i)])}
                className="absolute inset-x-0 bottom-0 bg-[#0A0B0E]/80 font-mono text-[8px] uppercase tracking-wider text-cyan-300 hover:bg-cyan-400/20"
              >
                Make cover
              </button>
            )}
            <button
              type="button"
              data-testid={`product-form-remove-image-${i}`}
              onClick={() => onChange(images.filter((_, j) => j !== i))}
              className="absolute right-1 top-1 rounded-full bg-[#0A0B0E]/80 p-1 text-slate-300 hover:text-red-400"
            >
              <X size={10} />
            </button>
          </div>
        ))}
        {!images.length && (
          <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed border-white/15 text-slate-600">
            <ImageIcon size={18} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <input data-testid="product-form-image" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste an image URL and press Add →" className={inputCls} />
        <button type="button" data-testid="product-form-add-url-btn" onClick={addUrl} className="shrink-0 rounded-full border border-white/15 px-4 py-2.5 text-xs font-semibold text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300">Add</button>
        <input ref={inputRef} data-testid="product-form-image-file" type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        <button
          type="button"
          data-testid="product-form-upload-btn"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-cyan-400/40 px-4 py-2.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-50"
        >
          <Upload size={13} /> {uploading ? "Uploading..." : "Upload photos"}
        </button>
      </div>
    </div>
  );
}


const VIDEO_CHUNK = 4 * 1024 * 1024;

async function uploadVideoChunked(file, onProgress) {
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.ceil(file.size / VIDEO_CHUNK);
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("total", String(total));
    fd.append("chunk", file.slice(i * VIDEO_CHUNK, (i + 1) * VIDEO_CHUNK));
    await api.post("/admin/upload-video/chunk", fd);
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  const done = new FormData();
  done.append("upload_id", uploadId);
  done.append("filename", file.name);
  done.append("content_type", file.type || "video/mp4");
  const { data } = await api.post("/admin/upload-video/complete", done);
  return data.url;
}

function VideoField({ videos, onChange }) {
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) {
      toast.error("Video is larger than 60MB — please compress it first");
      return;
    }
    setProgress(0);
    try {
      const url = await uploadVideoChunked(file, setProgress);
      onChange([...videos, url]);
      toast.success("Video added — press Save to publish it");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="space-y-3 sm:col-span-2" data-testid="product-form-videos">
      <div className="flex flex-wrap gap-3">
        {videos.map((src, i) => (
          <div key={src + i} data-testid={`product-form-video-${i}`} className="relative h-20 w-32 overflow-hidden rounded-xl border border-white/10 bg-[#0A0B0E]/60">
            <video src={src} muted className="h-full w-full object-cover" />
            <button
              type="button"
              data-testid={`product-form-remove-video-${i}`}
              onClick={() => onChange(videos.filter((_, j) => j !== i))}
              className="absolute right-1 top-1 rounded-full bg-[#0A0B0E]/80 p-1 text-slate-300 hover:text-red-400"
            >
              <X size={10} />
            </button>
          </div>
        ))}
        {!videos.length && (
          <div className="flex h-20 w-32 items-center justify-center rounded-xl border border-dashed border-white/15 text-slate-600">
            <Video size={18} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <input ref={inputRef} data-testid="product-form-video-file" type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upload} />
        <button
          type="button"
          data-testid="product-form-upload-video-btn"
          disabled={progress !== null}
          onClick={() => inputRef.current?.click()}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-fuchsia-400/40 px-4 py-2.5 text-xs font-semibold text-fuchsia-300 hover:bg-fuchsia-400/10 disabled:opacity-50"
        >
          <Upload size={13} /> {progress !== null ? `Uploading ${progress}%` : "Upload video"}
        </button>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">MP4 / WEBM / MOV · up to 60MB</span>
      </div>
    </div>
  );
}

function BulkUpload({ onDone }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const groups = {};
    files.forEach((f) => {
      const stem = f.name.replace(/\.[^.]+$/, "");
      const base = stem.replace(/[-_ ]\d{1,2}$/, "").toUpperCase();
      (groups[base] = groups[base] || []).push(f);
    });
    const batches = [];
    Object.values(groups).forEach((g) => {
      const last = batches[batches.length - 1];
      if (last && last.length + g.length <= 8) last.push(...g);
      else batches.push([...g]);
    });
    const matched = [], unmatched = [];
    setResult(null);
    let done = 0;
    for (const batch of batches) {
      setProgress({ done, total: files.length });
      const fd = new FormData();
      batch.forEach((f) => fd.append("files", f));
      try {
        const { data } = await api.post("/admin/bulk-images", fd);
        matched.push(...data.matched);
        unmatched.push(...data.unmatched);
      } catch (err) {
        toast.error(formatApiError(err));
        unmatched.push(...batch.map((f) => f.name));
      }
      done += batch.length;
    }
    setProgress(null);
    setResult({ matched, unmatched });
    toast.success(`${matched.length} photo${matched.length === 1 ? "" : "s"} updated`);
    onDone();
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <input ref={inputRef} data-testid="bulk-upload-file-input" type="file" accept="image/*" multiple className="hidden" onChange={upload} />
      <button
        data-testid="admin-bulk-upload-btn"
        disabled={!!progress}
        onClick={() => inputRef.current?.click()}
        className="flex items-center gap-2 rounded-full border border-fuchsia-400/40 px-5 py-2 text-sm font-semibold text-fuchsia-300 transition-colors hover:bg-fuchsia-400/10 disabled:opacity-50"
      >
        <Images size={15} /> {progress ? `Uploading ${progress.done}/${progress.total}...` : "Bulk Upload Photos"}
      </button>
      {result && (
        <div data-testid="bulk-upload-result" className="w-full max-w-md rounded-xl border border-white/10 bg-[#12141A] p-3 text-xs text-slate-400">
          <span className="text-emerald-400" data-testid="bulk-upload-matched-count">{result.matched.length} matched</span>
          {result.unmatched.length > 0 && (
            <span data-testid="bulk-upload-unmatched"> · <span className="text-red-400">{result.unmatched.length} not matched:</span> {result.unmatched.join(", ")}</span>
          )}
          <button data-testid="bulk-upload-result-close" onClick={() => setResult(null)} className="ml-2 text-slate-500 hover:text-slate-300">✕</button>
        </div>
      )}
    </div>
  );
}

function ChangePasswordModal({ email, onClose, onEmailChange, isStaff = false }) {
  const [loginEmail, setLoginEmail] = useState(email || "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
        new_email: loginEmail.trim().toLowerCase(),
      });
      toast.success("Login updated — use it next time you sign in");
      onEmailChange?.(data.email);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0B0E]/80 p-4 backdrop-blur-sm" onClick={onClose} data-testid="change-password-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#12141A] p-7">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">{isStaff ? "My Password" : "Admin Login"}</h3>
          <button type="button" data-testid="change-password-close-btn" onClick={onClose} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-fuchsia-300"><X size={15} /></button>
        </div>
        <div className="mt-5 space-y-3">
          {!isStaff && (
            <input data-testid="login-email-input" type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="Login email" className={inputCls} />
          )}
          <input data-testid="current-password-input" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" className={inputCls} />
          <input data-testid="new-password-input" type="password" required value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password (min 8 characters)" className={inputCls} />
          <input data-testid="confirm-password-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password" className={inputCls} />
        </div>
        <button data-testid="change-password-submit-btn" type="submit" disabled={saving} className="mt-5 w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3 text-sm font-bold text-[#0A0B0E] disabled:opacity-50">
          {saving ? "Saving..." : isStaff ? "Update Password" : "Update Login"}
        </button>
        <p className="mt-3 font-mono text-[10px] leading-relaxed text-slate-500">Works on this site only — the live and preview sites keep separate logins.</p>
      </form>
    </div>
  );
}

function BackupModal({ onClose, onImported }) {
  const [busy, setBusy] = useState("");
  const [photosOnly, setPhotosOnly] = useState(true);
  const fileRef = useRef(null);

  const exportData = async () => {
    setBusy("export");
    try {
      const { data } = await api.get("/admin/export");
      data.source_url = window.location.origin;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `varsha-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${data.products.length} products and ${data.whatsapp_routes.length} WhatsApp desks`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const importData = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const warning = photosOnly
      ? "This copies photos onto matching product codes and replaces the WhatsApp desks on THIS site. Continue?"
      : "This REPLACES all products and WhatsApp desks on THIS site with the backup. Continue?";
    if (!window.confirm(warning)) return;
    setBusy("import");
    try {
      const payload = JSON.parse(await file.text());
      payload.mode = photosOnly ? "photos" : "replace";
      const { data } = await api.post("/admin/import", payload);
      toast.success(`${data.products} products updated, ${data.whatsapp_routes} desks, ${data.photos_copied} photos copied`);
      if (data.photos_failed) toast.error(`${data.photos_failed} photos could not be copied — re-upload them here`);
      onImported();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0B0E]/80 p-4 backdrop-blur-sm" onClick={onClose} data-testid="backup-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl border border-white/10 bg-[#12141A] p-7">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">Copy Site Data</h3>
          <button data-testid="backup-close-btn" onClick={onClose} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-fuchsia-300"><X size={15} /></button>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          The preview site and your live domain keep separate data. Export here, then open the other site's admin and import the file — products, photos, prices, Live status and WhatsApp desks all travel with it. Orders are never touched.
        </p>
        <div className="mt-6 space-y-3">
          <button
            data-testid="backup-export-btn"
            onClick={exportData}
            disabled={!!busy}
            className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3 text-sm font-bold text-[#0A0B0E] disabled:opacity-50"
          >
            {busy === "export" ? "Preparing..." : "Export backup file"}
          </button>
          <input ref={fileRef} data-testid="backup-import-input" type="file" accept="application/json,.json" className="hidden" onChange={importData} />
          <label className="flex items-start gap-2 rounded-xl border border-white/10 bg-[#0A0B0E]/50 p-3 text-xs text-slate-400">
            <input
              data-testid="backup-photos-only-checkbox"
              type="checkbox"
              checked={photosOnly}
              onChange={(e) => setPhotosOnly(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 accent-cyan-400"
            />
            Keep this site's prices, stock and Live status — copy only photos (matched by product code) and WhatsApp desks
          </label>
          <button
            data-testid="backup-import-btn"
            onClick={() => fileRef.current?.click()}
            disabled={!!busy}
            className="w-full rounded-full border border-white/15 py-3 text-sm font-semibold text-slate-300 hover:border-cyan-400/50 hover:text-cyan-300 disabled:opacity-50"
          >
            {busy === "import" ? "Importing, please wait..." : "Import backup file"}
          </button>
          <button
            data-testid="backup-cleanup-btn"
            onClick={async () => {
              setBusy("cleanup");
              try {
                const { data } = await api.post("/admin/cleanup-photos");
                toast.success(`${data.photos_removed} missing photos removed from ${data.products_fixed} products`);
                onImported();
              } catch (err) {
                toast.error(formatApiError(err));
              } finally {
                setBusy("");
              }
            }}
            disabled={!!busy}
            className="w-full rounded-full border border-white/15 py-3 text-sm font-semibold text-slate-300 hover:border-yellow-300/50 hover:text-yellow-300 disabled:opacity-50"
          >
            {busy === "cleanup" ? "Checking photos..." : "Fix missing photos"}
          </button>
          <button
            data-testid="backup-trim-btn"
            onClick={async () => {
              setBusy("trim");
              try {
                const { data } = await api.post("/admin/trim-photos");
                toast.success(`${data.photos_trimmed} of ${data.total} photos cropped tight`);
                onImported();
              } catch (err) {
                toast.error(formatApiError(err));
              } finally {
                setBusy("");
              }
            }}
            disabled={!!busy}
            className="w-full rounded-full border border-white/15 py-3 text-sm font-semibold text-slate-300 hover:border-fuchsia-400/50 hover:text-fuchsia-300 disabled:opacity-50"
          >
            {busy === "trim" ? "Cropping photos..." : "Remove black borders from photos"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HomepagePanel() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const fileRef = useRef(null);
  const slideImgRef = useRef(null);
  const slideVidRef = useRef(null);
  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  const addSlides = (items) => setForm((f) => ({ ...f, hero_slides: [...(f.hero_slides || []), ...items] }));

  const addSlideImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy("images");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("mode", "banner");
      const { data } = await api.post("/admin/upload-images", fd);
      addSlides(data.urls.map((url) => ({ url, type: "image", focus: 50 })));
      toast.success(`${data.urls.length} slide${data.urls.length === 1 ? "" : "s"} added — press Save`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const addSlideVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) {
      toast.error("Video is larger than 60MB — please compress it first");
      return;
    }
    setBusy("video0");
    try {
      const url = await uploadVideoChunked(file, (pct) => setBusy(`video${pct}`));
      addSlides([{ url, type: "video", focus: 50 }]);
      toast.success("Video slide added — press Save");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "banner");
      const { data } = await api.post("/admin/upload-image", fd);
      setForm((f) => ({ ...f, hero_image: data.url }));
      toast.success("Image uploaded — press Save to publish it");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Homepage updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="mt-5 text-sm text-slate-500">Loading...</div>;

  return (
    <div className="mt-5 grid gap-4 lg:grid-cols-[1.1fr_1fr]" data-testid="admin-homepage-panel">
      <div className="space-y-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Top banner slideshow (photos & videos, 6s each)</p>
        <div className="flex flex-wrap gap-3" data-testid="hero-slides-list">
          {(form.hero_slides || []).map((sl, i) => (
            <div key={sl.url + i} data-testid={`hero-slide-${i}`} onClick={() => setActiveSlide(i)} className={`relative h-20 w-32 cursor-pointer overflow-hidden rounded-xl border bg-[#0A0B0E]/60 ${i === activeSlide ? "border-cyan-400" : "border-white/10"}`}>
              {sl.type === "video" ? (
                <video src={sl.url} muted className="h-full w-full object-cover" />
              ) : (
                <img src={sl.url} alt="" className="h-full w-full object-cover" />
              )}
              {i === 0 ? (
                <span className="absolute inset-x-0 bottom-0 bg-cyan-400/90 text-center font-mono text-[8px] uppercase tracking-wider text-[#0A0B0E]">First</span>
              ) : (
                <button
                  type="button"
                  data-testid={`hero-slide-make-first-${i}`}
                  onClick={() => setForm((f) => ({ ...f, hero_slides: [f.hero_slides[i], ...f.hero_slides.filter((_, j) => j !== i)] }))}
                  className="absolute inset-x-0 bottom-0 bg-[#0A0B0E]/80 font-mono text-[8px] uppercase tracking-wider text-cyan-300 hover:bg-cyan-400/20"
                >
                  Make first
                </button>
              )}
              <button
                type="button"
                data-testid={`hero-slide-remove-${i}`}
                onClick={() => setForm((f) => ({ ...f, hero_slides: f.hero_slides.filter((_, j) => j !== i) }))}
                className="absolute right-1 top-1 rounded-full bg-[#0A0B0E]/80 p-1 text-slate-300 hover:text-red-400"
              >
                <X size={10} />
              </button>
              {sl.type === "video" && (
                <span className="absolute left-1 top-1 rounded-full bg-[#0A0B0E]/80 px-2 font-mono text-[8px] uppercase text-fuchsia-300">Video</span>
              )}
            </div>
          ))}
          {!form.hero_slides?.length && (
            <div className="flex h-20 w-32 items-center justify-center rounded-xl border border-dashed border-white/15 text-center font-mono text-[9px] uppercase tracking-[0.15em] text-slate-600">
              Single image
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={slideImgRef} data-testid="hero-slide-image-file" type="file" accept="image/*" multiple className="hidden" onChange={addSlideImages} />
          <button data-testid="hero-slide-add-images-btn" onClick={() => slideImgRef.current?.click()} disabled={busy !== ""} className="flex items-center gap-1.5 rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-50">
            <Upload size={13} /> {busy === "images" ? "Uploading..." : "Add photos"}
          </button>
          <input ref={slideVidRef} data-testid="hero-slide-video-file" type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={addSlideVideo} />
          <button data-testid="hero-slide-add-video-btn" onClick={() => slideVidRef.current?.click()} disabled={busy !== ""} className="flex items-center gap-1.5 rounded-full border border-fuchsia-400/40 px-4 py-2 text-xs font-semibold text-fuchsia-300 hover:bg-fuchsia-400/10 disabled:opacity-50">
            <Upload size={13} /> {busy.startsWith("video") ? busy.replace("video", "Uploading ") + "%" : "Add video"}
          </button>
          <span className="font-mono text-[10px] text-slate-600">Photos 15MB · videos 60MB</span>
        </div>
        {form.hero_slides?.[activeSlide] && (
          <div className="space-y-2 rounded-2xl border border-white/10 bg-[#0A0B0E]/40 p-3" data-testid="hero-slide-preview-box">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Slide {activeSlide + 1} preview — 16:9 as shown on the website
            </p>
            <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl border border-white/10 bg-[#12141A]">
              {form.hero_slides[activeSlide].type === "video" ? (
                <video
                  data-testid="hero-slide-preview-video"
                  src={form.hero_slides[activeSlide].url}
                  muted
                  loop
                  autoPlay
                  playsInline
                  style={{ objectPosition: `center ${form.hero_slides[activeSlide].focus ?? 50}%` }}
                  className="h-full w-full object-cover"
                />
              ) : (
                <img
                  data-testid="hero-slide-preview-image"
                  src={form.hero_slides[activeSlide].url}
                  alt=""
                  style={{ objectPosition: `center ${form.hero_slides[activeSlide].focus ?? 50}%` }}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Frame</span>
              <input
                type="range"
                min="0"
                max="100"
                data-testid="hero-slide-focus-range"
                value={form.hero_slides[activeSlide].focus ?? 50}
                onChange={(e) => {
                  const focus = Number(e.target.value);
                  setForm((f) => ({ ...f, hero_slides: f.hero_slides.map((sl, j) => (j === activeSlide ? { ...sl, focus } : sl)) }));
                }}
                className="h-1 flex-1 accent-cyan-400"
              />
              <span className="w-10 font-mono text-[10px] text-cyan-300">{form.hero_slides[activeSlide].focus ?? 50}%</span>
            </div>
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-600">Drag to choose which part of a tall photo stays visible, then Save</p>
          </div>
        )}
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Fallback image (used when no slides above)</p>
        <div className="flex items-center gap-3">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[#0A0B0E]/60">
            {form.hero_image ? (
              <img data-testid="hero-image-preview" src={form.hero_image} alt="" className="h-full w-full object-cover" />
            ) : (
              <ImageIcon size={18} className="m-auto mt-9 text-slate-600" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <input data-testid="hero-image-input" value={form.hero_image} onChange={set("hero_image")} placeholder="Image URL, or upload →" className={inputCls} />
            <div className="flex gap-2">
              <input ref={fileRef} data-testid="hero-image-file" type="file" accept="image/*" className="hidden" onChange={upload} />
              <button data-testid="hero-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-50">
                <Upload size={13} /> {uploading ? "Uploading..." : "Upload image"}
              </button>
              <span className="self-center font-mono text-[10px] text-slate-600">JPG/PNG up to 15MB</span>
              {form.hero_image && (
                <button data-testid="hero-image-reset-btn" onClick={() => setForm((f) => ({ ...f, hero_image: "" }))} className="rounded-full border border-white/10 px-4 py-2 text-xs text-slate-400 hover:text-red-400">
                  Use default
                </button>
              )}
            </div>
          </div>
        </div>
        <input data-testid="hero-caption-input" value={form.hero_caption} onChange={set("hero_caption")} placeholder="Caption on the image e.g. Diwali Offer Live" className={inputCls} />
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="hero-badge1-input" value={form.hero_badge_1} onChange={set("hero_badge_1")} placeholder="Left chip e.g. 385°F or 10% OFF" className={inputCls} />
          <input data-testid="hero-badge2-input" value={form.hero_badge_2} onChange={set("hero_badge_2")} placeholder="Right chip e.g. 180s or Till 30 Nov" className={inputCls} />
        </div>
        <input data-testid="hero-offer-title-input" value={form.hero_offer_title} onChange={set("hero_offer_title")} placeholder="Floating card title (blank = Full Colour / CMYK)" className={inputCls} />
        <input data-testid="hero-offer-text-input" value={form.hero_offer_text} onChange={set("hero_offer_text")} placeholder="Floating card text e.g. Free shipping above ₹10,000" className={inputCls} />
        <button data-testid="save-homepage-btn" onClick={save} disabled={saving} className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3 text-sm font-bold text-[#0A0B0E] disabled:opacity-50">
          {saving ? "Saving..." : "Save & publish to homepage"}
        </button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Live preview</p>
        <div className="relative mt-4 overflow-hidden rounded-2xl border border-white/10">
          <img src={form.hero_image || "/hero-sublimation.jpg"} alt="" onError={(e) => { e.currentTarget.src = "/placeholder-product.svg"; }} className="h-56 w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">{form.hero_caption || "Sublimation in Action"}</span>
            <span className="flex gap-2">
              {form.hero_badge_1 && <span className="rounded-full bg-white/10 px-2.5 py-1 font-mono text-[10px] text-yellow-300">{form.hero_badge_1}</span>}
              {form.hero_badge_2 && <span className="rounded-full bg-white/10 px-2.5 py-1 font-mono text-[10px] text-fuchsia-300">{form.hero_badge_2}</span>}
            </span>
          </div>
          {(form.hero_offer_title || form.hero_offer_text) && (
            <div className="absolute right-3 top-3 rounded-xl border border-white/10 bg-[#181B24]/90 px-3 py-2 backdrop-blur-xl">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{form.hero_offer_title || "Full Colour"}</div>
              <div className="font-display text-sm font-bold text-cyan-300">{form.hero_offer_text || "CMYK"}</div>
            </div>
          )}
        </div>
        <p className="mt-4 text-xs leading-relaxed text-slate-500">
          Use this for festival offers, new arrivals or price-drop announcements — customers see it at the top of the homepage the moment you save.
        </p>
      </div>
    </div>
  );
}

function ContentPanel() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (key, i, field) => (e) =>
    setForm((f) => ({ ...f, [key]: f[key].map((row, j) => (j === i ? { ...row, [field]: e.target.value } : row)) }));
  const addItem = (key, blank) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), blank] }));
  const removeItem = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Sections updated on the website");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="mt-5 text-sm text-slate-500">Loading...</div>;

  return (
    <div className="mt-5 space-y-4" data-testid="admin-content-panel">
      <div className="space-y-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Website menu links</p>
        <div className="space-y-2">
          {(form.nav_links || []).map((l, i) => (
            <div key={i} data-testid={`nav-link-row-${i}`} className="grid gap-2 rounded-xl border border-white/10 bg-[#0A0B0E]/40 p-3 sm:grid-cols-[1fr_1.2fr_auto]">
              <input data-testid={`nav-link-label-${i}`} value={l.label} onChange={setItem("nav_links", i, "label")} placeholder="Menu name e.g. Mugs" className={inputCls} />
              <input data-testid={`nav-link-href-${i}`} value={l.href} onChange={setItem("nav_links", i, "href")} placeholder="#catalog or https://..." className={inputCls} />
              <button type="button" data-testid={`nav-link-remove-${i}`} onClick={() => removeItem("nav_links", i)} className="flex h-11 w-11 items-center justify-center justify-self-start rounded-full border border-white/10 text-slate-400 hover:text-red-400">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <button type="button" data-testid="nav-link-add-btn" onClick={() => addItem("nav_links", { label: "", href: "#catalog" })} className="flex h-11 items-center gap-1.5 rounded-full border border-cyan-400/40 px-4 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10">
          <Plus size={13} /> Add menu link
        </button>
        <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-600">
          Use #catalog, #new-launch, #manifesto, #heat-press, a category name like #mdfsheets (opens that category in the catalogue), or any full web address
        </p>
      </div>

      <div className="space-y-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Manifesto section</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="manifesto-eyebrow-input" value={form.manifesto_eyebrow || ""} onChange={set("manifesto_eyebrow")} placeholder="Small label e.g. The Manifesto" className={inputCls} />
          <input data-testid="manifesto-title-input" value={form.manifesto_title || ""} onChange={set("manifesto_title")} placeholder="Heading e.g. Why serious printers source here" className={inputCls} />
        </div>
        {(form.manifesto_cards || []).map((c, i) => (
          <div key={i} data-testid={`manifesto-card-${i}`} className="space-y-2 rounded-xl border border-white/10 bg-[#0A0B0E]/40 p-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">Card {String(i + 1).padStart(2, "0")}</span>
              <button type="button" data-testid={`manifesto-card-remove-${i}`} onClick={() => removeItem("manifesto_cards", i)} className="rounded-full border border-white/10 p-1.5 text-slate-400 hover:text-red-400">
                <Trash2 size={12} />
              </button>
            </div>
            <input data-testid={`manifesto-card-title-${i}`} value={c.title} onChange={setItem("manifesto_cards", i, "title")} placeholder="Card title" className={inputCls} />
            <textarea data-testid={`manifesto-card-desc-${i}`} value={c.description} onChange={setItem("manifesto_cards", i, "description")} placeholder="Card description" rows={2} className={`${inputCls} resize-none`} />
          </div>
        ))}
        <button type="button" data-testid="manifesto-card-add-btn" onClick={() => addItem("manifesto_cards", { title: "", description: "" })} className="flex items-center gap-1.5 rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10">
          <Plus size={13} /> Add card
        </button>
      </div>

      <div className="space-y-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Heat press specs section</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="press-eyebrow-input" value={form.press_eyebrow || ""} onChange={set("press_eyebrow")} placeholder="Small label e.g. Press Recipes" className={inputCls} />
          <input data-testid="press-title-input" value={form.press_title || ""} onChange={set("press_title")} placeholder="Heading e.g. Heat Press Reference Matrix" className={inputCls} />
        </div>
        <div className="space-y-2">
          {(form.press_rows || []).map((r, i) => (
            <div key={i} data-testid={`press-row-${i}`} className="grid gap-2 rounded-xl border border-white/10 bg-[#0A0B0E]/40 p-3 sm:grid-cols-[1.4fr_1fr_1fr_1fr_auto]">
              <input data-testid={`press-row-product-${i}`} value={r.product} onChange={setItem("press_rows", i, "product")} placeholder="Product e.g. Ceramic Mugs" className={inputCls} />
              <input data-testid={`press-row-temp-${i}`} value={r.temp} onChange={setItem("press_rows", i, "temp")} placeholder="Temp" className={inputCls} />
              <input data-testid={`press-row-time-${i}`} value={r.time} onChange={setItem("press_rows", i, "time")} placeholder="Time" className={inputCls} />
              <input data-testid={`press-row-pressure-${i}`} value={r.pressure} onChange={setItem("press_rows", i, "pressure")} placeholder="Pressure" className={inputCls} />
              <button type="button" data-testid={`press-row-remove-${i}`} onClick={() => removeItem("press_rows", i)} className="self-center rounded-full border border-white/10 p-2 text-slate-400 hover:text-red-400">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <button type="button" data-testid="press-row-add-btn" onClick={() => addItem("press_rows", { product: "", temp: "", time: "", pressure: "" })} className="flex items-center gap-1.5 rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10">
          <Plus size={13} /> Add row
        </button>
      </div>

      <button data-testid="save-content-btn" onClick={save} disabled={saving} className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3 text-sm font-bold text-[#0A0B0E] disabled:opacity-50">
        {saving ? "Saving..." : "Save & publish to website"}
      </button>
    </div>
  );
}

const CRM_STATUS = {
  sent: { label: "In CRM", cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  duplicate: { label: "Already in CRM", cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" },
  waiting_crm: { label: "Waiting on open lead", cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  pending: { label: "Queued", cls: "border-yellow-300/40 bg-yellow-300/10 text-yellow-300" },
  failed: { label: "Failed", cls: "border-red-400/40 bg-red-400/10 text-red-300" },
};

function fmtDate(iso, withTime = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  const tz = { timeZone: "Asia/Kolkata" };
  const date = d.toLocaleDateString("en-IN", { ...tz, day: "2-digit", month: "short", year: "2-digit" });
  return withTime ? `${date}, ${d.toLocaleTimeString("en-IN", { ...tz, hour: "numeric", minute: "2-digit" })}` : date;
}

function DetailRow({ label, value, testid, mono = false }) {
  return (
    <div className="flex gap-3 py-1.5 text-xs">
      <span className="w-32 shrink-0 font-mono uppercase tracking-[0.15em] text-slate-500">{label}</span>
      <span data-testid={testid} className={`flex-1 break-words text-slate-300 ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function CrmCustomerCard({ p, onRetry, busy, isStaff }) {
  const [open, setOpen] = useState(false);
  const meta = p.internal
    ? { label: "Our own number", cls: "border-white/15 text-slate-500" }
    : CRM_STATUS[p.crm.status] || { label: "Not in CRM yet", cls: "border-white/15 text-slate-500" };
  return (
    <div data-testid={`crm-customer-${p.id}`} className="rounded-2xl border border-white/10 bg-[#12141A]">
      <button
        data-testid={`crm-customer-toggle-${p.id}`}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full flex-wrap items-center gap-3 px-5 py-4 text-left"
      >
        <div className="min-w-[180px] flex-1">
          <div className="font-medium text-slate-100">{p.full_name}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
            {p.company_name || "No company"} · {p.city || "—"}, {p.state}
          </div>
          {p.last_activity_at && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-400/80" data-testid={`crm-customer-updated-${p.id}`}>
              {p.last_activity_kind} · {timeAgo(p.last_activity_at)}
            </div>
          )}
        </div>
        <a
          href={`tel:${p.phone}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`crm-customer-phone-${p.id}`}
          className="font-mono text-xs text-cyan-300 hover:underline"
        >
          {p.phone}
        </a>
        <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
          {!!p.carts?.length && (
            <span data-testid={`crm-customer-cart-${p.id}`} className={`rounded-full border px-2.5 py-1 ${p.carts.some((cart) => cart.status === "follow_up") ? "border-amber-300/40 bg-amber-300/10 text-amber-200" : "border-cyan-400/30 text-cyan-300"}`}>
              {p.carts.some((cart) => cart.status === "follow_up") ? "Cart follow-up due" : `${p.carts.reduce((sum, cart) => sum + cart.item_count, 0)} pieces in cart`}
            </span>
          )}
          <span className="rounded-full border border-white/10 px-2.5 py-1 text-slate-400" data-testid={`crm-customer-searches-${p.id}`}>
            {p.searches.length} searches
          </span>
          <span className="rounded-full border border-white/10 px-2.5 py-1 text-slate-400" data-testid={`crm-customer-views-${p.id}`}>
            {p.views.length} viewed
          </span>
          <span className="rounded-full border border-white/10 px-2.5 py-1 text-slate-400" data-testid={`crm-customer-orders-${p.id}`}>
            {p.orders.length} orders
          </span>
          <span className={`rounded-full border px-2.5 py-1 ${meta.cls}`} data-testid={`crm-customer-status-${p.id}`}>{meta.label}</span>
        </div>
        <ChevronDown size={15} className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="grid gap-5 border-t border-white/10 px-5 py-5 lg:grid-cols-2" data-testid={`crm-customer-detail-${p.id}`}>
          <CrmCartDetails customer={p} />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-400">Profile</p>
            <div className="mt-2">
              <DetailRow label="Name" value={p.full_name} testid={`crm-detail-name-${p.id}`} />
              <DetailRow label="Company" value={p.company_name} />
              <DetailRow label="Mobile" value={p.phone} mono />
              <DetailRow label="Address" value={[p.address, [p.city, p.pincode].filter(Boolean).join(" — "), p.state].filter(Boolean).join(", ")} testid={`crm-detail-address-${p.id}`} />
              <DetailRow label="GST number" value={p.gstin} mono />
              <DetailRow label="Registered" value={fmtDate(p.created_at)} testid={`crm-detail-registered-${p.id}`} />
              <DetailRow label="Last login" value={`${fmtDate(p.last_login_at, true)}${p.login_count ? ` · ${p.login_count} logins` : ""}`} testid={`crm-detail-lastlogin-${p.id}`} />
              <DetailRow label="Staff note" value={p.note ? `${p.note}${p.note_by ? ` (by ${p.note_by})` : ""}` : ""} />
            </div>

            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-400">Orders · {formatINR(p.order_total)}</p>
            <div className="mt-2 space-y-2" data-testid={`crm-detail-orders-${p.id}`}>
              {p.orders.length ? p.orders.map((o) => (
                <div key={o.id} className="rounded-xl border border-white/10 p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-mono uppercase tracking-[0.15em] text-slate-500">{fmtDate(o.created_at)} · {o.status}</span>
                    <span className="font-mono font-bold text-yellow-300">{formatINR(o.total)}</span>
                  </div>
                  <div className="mt-1.5 whitespace-pre-wrap text-slate-400 [overflow-wrap:anywhere]">
                    {o.items.map((i) => `${i.sku ? i.sku + " " : ""}${i.name} x${i.qty}${itemDetailsText(i) ? ` · ${itemDetailsText(i)}` : ""}`).join(", ")}
                  </div>
                </div>
              )) : <p className="text-xs text-slate-600">No orders yet</p>}
            </div>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">Searched for ({p.searches.length})</p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`crm-detail-searches-${p.id}`}>
              {p.searches.length ? p.searches.map((s, i) => (
                <span key={i} title={`Last ${fmtDate(s.last, true)}`} className="rounded-full border border-cyan-400/25 bg-cyan-400/5 px-3 py-1 text-xs text-slate-300">
                  {s.value}{s.count > 1 ? ` ×${s.count}` : ""}
                </span>
              )) : <p className="text-xs text-slate-600">No searches recorded</p>}
            </div>

            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">Products opened ({p.views.length})</p>
            <div className="mt-2 space-y-1" data-testid={`crm-detail-views-${p.id}`}>
              {p.views.length ? p.views.map((v, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 px-3 py-1.5 text-xs">
                  <span className="truncate text-slate-300">{v.sku && <span className="font-mono text-cyan-300">{v.sku} </span>}{v.value}</span>
                  <span className="shrink-0 font-mono text-[10px] text-slate-500">{v.count > 1 ? `×${v.count} · ` : ""}{fmtDate(v.last)}</span>
                </div>
              )) : <p className="text-xs text-slate-600">No products opened yet</p>}
            </div>

            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-400">Biziverse</p>
            <div className="mt-2">
              <DetailRow label="Status" value={p.internal ? "Our own owner / desk / staff number — never sent to Biziverse" : `${meta.label}${p.crm.message ? ` — ${p.crm.message}` : ""}`} testid={`crm-detail-status-${p.id}`} />
              <DetailRow label="Last push" value={fmtDate(p.crm.updated_at, true)} />
              <DetailRow label="Sent as" value={p.crm.needs} testid={`crm-detail-needs-${p.id}`} />
            </div>
            {p.crm.lead_id && p.crm.status !== "sent" && p.crm.status !== "duplicate" && (
              <button
                data-testid={`crm-detail-retry-${p.id}`}
                onClick={() => onRetry(p.crm.lead_id)}
                disabled={!!busy}
                className="mt-3 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 disabled:opacity-50"
              >
                {busy === p.crm.lead_id ? "Sending..." : "Push to Biziverse now"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CrmPanel({ role = "admin", states = [] }) {
  const [profiles, setProfiles] = useState([]);
  const [leads, setLeads] = useState([]);
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [kind, setKind] = useState("all");
  const [period, setPeriod] = useState("all");
  const [showLog, setShowLog] = useState(false);
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState(null);
  const isStaff = role === "staff";

  const load = async () => {
    try {
      const [p, l] = await Promise.all([api.get("/admin/crm/customers"), api.get("/admin/crm/leads")]);
      setProfiles(p.data);
      setLeads(l.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.get("/admin/crm/test");
      setStatus(data);
      data.ok ? toast.success(data.message) : toast.error(data.message || "Biziverse rejected the key");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const backfill = async () => {
    setBusy("backfill");
    try {
      const { data } = await api.post("/admin/crm/backfill");
      toast.success(data.queued ? `${data.queued} earlier customer(s) queued for Biziverse` : "Every customer is already in the CRM queue");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const syncActivity = async () => {
    setBusy("activity");
    try {
      const { data } = await api.post("/admin/crm/sync-activity");
      toast.success(
        data.queued
          ? `${data.queued} customer(s) queued with their latest searches & views`
          : "Every customer's activity is already up to date in Biziverse"
      );
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const retry = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/crm/leads/${id}/retry`);
      setLeads((cur) => cur.map((l) => (l.id === id ? data : l)));
      toast.success(`Biziverse: ${data.message}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally {
      setBusy("");
    }
  };

  const counts = leads.reduce((acc, l) => ({ ...acc, [l.status]: (acc[l.status] || 0) + 1 }), {});
  const q = query.trim().toLowerCase();
  const stateOptions = Array.from(new Set(profiles.map((p) => p.state).filter(Boolean))).sort();
  const since = { today: 1, week: 7, month: 30 }[period];
  const visible = profiles.filter((p) => {
    if (q && ![p.full_name, p.company_name, p.phone, p.city, p.state, p.gstin, ...p.searches.map((s) => s.value), ...p.views.map((v) => `${v.sku} ${v.value}`), ...(p.carts || []).flatMap((cart) => cart.items.map((item) => `${item.sku} ${item.name}`))]
      .join(" ").toLowerCase().includes(q)) return false;
    if (stateFilter !== "all" && p.state !== stateFilter) return false;
    if (since && (!p.last_activity_at || (Date.now() - new Date(p.last_activity_at)) / 86400000 > since)) return false;
    if (kind === "ordered" && !p.orders.length) return false;
    if (kind === "no-order" && p.orders.length) return false;
    if (kind === "cart" && !p.carts?.length) return false;
    if (kind === "cart-follow-up" && !p.carts?.some((cart) => cart.status === "follow_up")) return false;
    if (kind === "searched" && !p.searches.length && !p.views.length) return false;
    if (kind === "internal" && !p.internal) return false;
    if (kind === "pending" && !["pending", "waiting_crm", "failed"].includes(p.crm.status)) return false;
    if (kind === "in-crm" && !["sent", "duplicate"].includes(p.crm.status)) return false;
    return true;
  });
  const selectCls = "rounded-xl border border-white/10 bg-[#12141A] px-3 py-2.5 text-xs text-slate-300 focus:border-cyan-400/50 focus:outline-none";

  return (
    <div className="mt-5 space-y-4" data-testid="admin-crm-panel">
      <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Customer intelligence &amp; Biziverse</p>
            <p className="mt-1 text-sm text-slate-300">
              {isStaff
                ? "Open any customer to see their searches, products, orders and what went to Biziverse."
                : "Every customer with their searches, products opened, orders, logins and CRM status. Tap a row to open the full file."}
            </p>
            {isStaff && (
              <p className="mt-2 text-xs text-cyan-300" data-testid="staff-crm-scope-note">
                Showing {states.length ? states.join(", ") : "no state yet"} only.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {!isStaff && (
              <>
                <button data-testid="crm-test-btn" onClick={test} disabled={!!busy} className="rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-50">
                  {busy === "test" ? "Checking..." : "Test connection"}
                </button>
                <button data-testid="crm-backfill-btn" onClick={backfill} disabled={!!busy} className="rounded-full border border-fuchsia-400/40 px-4 py-2 text-xs font-semibold text-fuchsia-300 hover:bg-fuchsia-400/10 disabled:opacity-50">
                  {busy === "backfill" ? "Queueing..." : "Send earlier customers"}
                </button>
              </>
            )}
            <button data-testid="crm-sync-activity-btn" onClick={syncActivity} disabled={!!busy} className="rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-50">
              {busy === "activity" ? "Syncing…" : "Sync searches & views"}
            </button>
            <button data-testid="crm-refresh-btn" onClick={load} disabled={!!busy} className="rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-cyan-400/40 disabled:opacity-50">
              Refresh
            </button>
          </div>
        </div>
        {status && (
          <p data-testid="crm-test-result" className={`mt-4 font-mono text-[11px] ${status.ok ? "text-emerald-300" : "text-red-300"}`}>
            {status.ok ? "CONNECTED" : "PROBLEM"} · code {String(status.code)} · {status.message}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
          <button data-testid="crm-filter-carts-btn" onClick={() => setKind("cart")} className="rounded-full border border-cyan-400/30 px-3 py-2 text-cyan-300 transition-colors hover:bg-cyan-400/10">
            Customers with carts: {profiles.filter((p) => p.carts?.length).length}
          </button>
          <button data-testid="crm-filter-follow-up-btn" onClick={() => setKind("cart-follow-up")} className="rounded-full border border-amber-300/40 px-3 py-2 text-amber-200 transition-colors hover:bg-amber-300/10">
            Cart follow-up due: {profiles.filter((p) => p.carts?.some((cart) => cart.status === "follow_up")).length}
          </button>
          <span className="rounded-full border border-white/15 px-3 py-1 text-slate-300" data-testid="crm-customer-count">
            Customers: {profiles.length}
          </span>
          {Object.entries(CRM_STATUS).map(([key, meta]) => (
            <span key={key} data-testid={`crm-count-${key}`} className={`rounded-full border px-3 py-1 ${meta.cls}`}>
              {meta.label}: {counts[key] || 0}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          data-testid="crm-customer-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a customer, company, mobile, city, GST or what they searched..."
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-[#12141A] px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
        />
        <select data-testid="crm-filter-period" value={period} onChange={(e) => setPeriod(e.target.value)} className={selectCls}>
          <option value="all">Any time</option>
          <option value="today">Updated today</option>
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
        </select>
        <select data-testid="crm-filter-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={selectCls}>
          <option value="all">All customers</option>
          <option value="ordered">Has ordered</option>
          <option value="no-order">Never ordered</option>
          <option value="cart">Has an unsubmitted cart</option>
          <option value="cart-follow-up">Cart follow-up due (24h)</option>
          <option value="searched">Searched or viewed</option>
          <option value="in-crm">In Biziverse</option>
          <option value="pending">Waiting for Biziverse</option>
          <option value="internal">Our own numbers</option>
        </select>
        <select data-testid="crm-filter-state" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className={selectCls}>
          <option value="all">All states</option>
          {stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(query || kind !== "all" || period !== "all" || stateFilter !== "all") && (
          <button
            data-testid="crm-filter-clear"
            onClick={() => { setQuery(""); setKind("all"); setPeriod("all"); setStateFilter("all"); }}
            className="rounded-xl border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"
          >
            Clear
          </button>
        )}
      </div>

      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="crm-visible-count">
        Showing {visible.length} of {profiles.length} · newest changes first
      </p>

      <div className="space-y-2.5" data-testid="crm-customer-list">
        {visible.map((p) => <CrmCustomerCard key={p.id} p={p} onRetry={retry} busy={busy} isStaff={isStaff} />)}
        {!visible.length && (
          <p className="rounded-2xl border border-white/10 bg-[#12141A] px-5 py-10 text-center text-sm text-slate-500" data-testid="crm-empty-state">
            {profiles.length ? "No customer matches that search." : "No customers yet — they appear here as soon as someone enters the catalogue."}
          </p>
        )}
      </div>

      <button
        data-testid="crm-toggle-log-btn"
        onClick={() => setShowLog((v) => !v)}
        className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-cyan-300"
      >
        {showLog ? "Hide" : "Show"} Biziverse push log ({leads.length})
      </button>

      {showLog && (
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[720px] text-left text-sm" data-testid="crm-leads-table">
            <thead>
              <tr className="border-b border-white/10 bg-[#12141A] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <th className="px-5 py-3">Lead</th>
                <th className="px-5 py-3">Mobile</th>
                <th className="px-5 py-3">City / State</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const meta = CRM_STATUS[l.status] || CRM_STATUS.pending;
                return (
                  <tr key={l.id} data-testid={`crm-lead-row-${l.id}`} className="border-b border-white/5">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-200">{[l.fields?.firstName, l.fields?.lastName].filter(Boolean).join(" ")}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{l.fields?.companyName || "—"}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${l.kind === "activity" ? "border-cyan-400/40 text-cyan-300" : "border-white/15 text-slate-500"}`}>
                          {l.kind === "activity" ? "Activity update" : "New signup"}
                        </span>
                        {l.fields?.needs && <span className="max-w-[260px] truncate text-[11px] text-slate-500" title={l.fields.needs}>{l.fields.needs}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-300">{l.fields?.mobile}</td>
                    <td className="px-5 py-3 text-xs text-slate-400">{[l.fields?.city, l.fields?.state].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${meta.cls}`}>{meta.label}</span>
                      <div className="mt-1 font-mono text-[10px] text-slate-500">{l.message}</div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {l.status !== "sent" && l.status !== "duplicate" && l.code !== 1 && (
                        <button data-testid={`crm-retry-btn-${l.id}`} onClick={() => retry(l.id)} disabled={!!busy} className="rounded-full border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 disabled:opacity-50">
                          {busy === l.id ? "Sending..." : "Retry"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!leads.length && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-500">Nothing pushed yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "Yesterday" : `${days} days ago`;
}

function QuickAddRows({ categories, defaultCategory, onDone }) {
  const blank = { size: "", sku: "", price: "", bulk_price: "" };
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prefix, setPrefix] = useState("MDF Sheet");
  const [category, setCategory] = useState(defaultCategory);
  const [bulkMin, setBulkMin] = useState("50");
  const [rows, setRows] = useState([{ ...blank }, { ...blank }, { ...blank }]);

  const setRow = (i, patch) => setRows((r) => r.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  const addRow = () => setRows((r) => [...r, { ...blank }]);
  const removeRow = (i) => setRows((r) => (r.length === 1 ? r : r.filter((_, j) => j !== i)));

  const filled = rows.filter((r) => r.size.trim() && String(r.price).trim());

  const save = async () => {
    if (!filled.length) {
      toast.error("Fill at least one row (size + price)");
      return;
    }
    setSaving(true);
    try {
      const payload = filled.map((r) => ({
        name: `${prefix.trim()} ${r.size.trim()}`.trim(),
        sku: r.sku.trim() || null,
        category,
        price: Number(r.price),
        bulk_price: r.bulk_price ? Number(r.bulk_price) : null,
        bulk_min: r.bulk_price && bulkMin ? Number(bulkMin) : null,
        description: "",
        image: "",
        images: [],
        videos: [],
        substrate: [],
        press_temp: "",
        press_time: "",
        press_pressure: "",
        stock: 0,
        featured: false,
        is_live: true,
        is_new_launch: false,
      }));
      const { data } = await api.post("/products/bulk", payload);
      toast.success(`${data.length} blank${data.length === 1 ? "" : "s"} added to ${category}`);
      setRows([{ ...blank }, { ...blank }, { ...blank }]);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  if (!open) {
    return (
      <button
        data-testid="admin-quick-add-btn"
        onClick={() => { setCategory(defaultCategory); setOpen(true); }}
        className="flex items-center gap-2 rounded-full border border-fuchsia-400/40 px-5 py-2 text-sm font-semibold text-fuchsia-300 transition-colors hover:bg-fuchsia-400/10"
      >
        <Rows3 size={15} /> Quick Add
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="quick-add-modal">
      <div className="my-6 w-full max-w-3xl rounded-3xl border border-white/10 bg-[#12141A] p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold">Quick add sizes</h3>
            <p className="mt-1 text-sm text-slate-500">Add many sizes of one blank in one go. Photos can be added later from the table.</p>
          </div>
          <button data-testid="quick-add-close-btn" onClick={() => setOpen(false)} className="text-slate-500 hover:text-red-400">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Name prefix</span>
            <input data-testid="quick-add-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} className={inputCls} placeholder="MDF Sheet" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Category</span>
            <select data-testid="quick-add-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
              {categories.map((c) => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Bulk from (pcs)</span>
            <input data-testid="quick-add-bulk-min" value={bulkMin} onChange={(e) => setBulkMin(e.target.value)} className={inputCls} placeholder="50" />
          </label>
        </div>

        <div className="mt-5 space-y-2" data-testid="quick-add-rows">
          <div className="hidden gap-2 px-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 sm:grid sm:grid-cols-[1.4fr_1fr_1fr_1fr_32px]">
            <span>Size / variant</span><span>Code</span><span>Price</span><span>Bulk price</span><span />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[1.4fr_1fr_1fr_1fr_32px] sm:items-center">
              <input data-testid={`quick-add-size-${i}`} value={r.size} onChange={(e) => setRow(i, { size: e.target.value })} className={inputCls} placeholder='8x10 inch' />
              <input data-testid={`quick-add-sku-${i}`} value={r.sku} onChange={(e) => setRow(i, { sku: e.target.value.toUpperCase() })} className={inputCls} placeholder="MS01" />
              <input data-testid={`quick-add-price-${i}`} value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} className={inputCls} placeholder="60" inputMode="decimal" />
              <input data-testid={`quick-add-bulk-price-${i}`} value={r.bulk_price} onChange={(e) => setRow(i, { bulk_price: e.target.value })} className={inputCls} placeholder="48" inputMode="decimal" />
              <button
                data-testid={`quick-add-remove-${i}`}
                onClick={() => removeRow(i)}
                aria-label="Remove row"
                className="flex h-9 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:text-red-400"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <button data-testid="quick-add-row-btn" onClick={addRow} className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-cyan-300 hover:text-cyan-200">
            <Plus size={14} /> Add row
          </button>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] text-slate-500" data-testid="quick-add-count">{filled.length} ready</span>
            <button
              data-testid="quick-add-save-btn"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-6 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-50"
            >
              {saving ? "Saving…" : `Save ${filled.length || ""} blanks`.trim()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_OFFER = { title: "", message: "", image: "", cta_label: "Shop now", link_type: "none", link_value: "", starts_on: "", ends_on: "", active: true, deal_type: "item_percent", discount_percent: 0, product_ids: [], bundle_price: 0, bundle_qty: {}, cart_min_total: 0, cart_discount_amount: 0 };

const DEAL_TYPES = {
  item_percent: "% off each listed product",
  cart_percent: "% off the total of listed products",
  cart_amount: "Rupees off the order total",
  bundle: "Combo set at one fixed price",
};

function OffersPanel({ categories, products }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [collections, setCollections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const fileRef = useRef(null);
  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  const load = useCallback(() => {
    api.get("/admin/offers").then(({ data }) => setList(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/collections?all=true").then(({ data }) => setCollections(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "banner");
      const { data } = await api.post("/admin/upload-image", fd);
      setEditing((f) => ({ ...f, image: data.url }));
      toast.success("Offer image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    if (!editing.title.trim()) return toast.error("Give the offer a title");
    setSaving(true);
    try {
      const payload = { ...editing, title: editing.title.trim(), message: editing.message.trim() };
      delete payload.id;
      delete payload.created_at;
      if (editing.id) await api.put(`/admin/offers/${editing.id}`, payload);
      else await api.post("/admin/offers", payload);
      toast.success(`${payload.title} saved`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (o) => {
    try {
      await api.put(`/admin/offers/${o.id}`, { ...o, active: !o.active, id: undefined });
      toast.success(`${o.title} ${o.active ? "switched off" : "switched on"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete the offer "${o.title}"?`)) return;
    try {
      await api.delete(`/admin/offers/${o.id}`);
      toast.success("Offer deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const badge = (o) => {
    if (!o.active) return { label: "Off", cls: "border-white/15 text-slate-500" };
    if (o.ends_on && o.ends_on < today) return { label: "Expired", cls: "border-red-400/40 text-red-300" };
    if (o.starts_on && o.starts_on > today) return { label: "Scheduled", cls: "border-yellow-400/40 text-yellow-300" };
    return { label: "Live", cls: "border-emerald-400/40 text-emerald-300" };
  };

  return (
    <div className="mt-5 space-y-4" data-testid="admin-offers-panel">
      <DiscountCodes />
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Festive offers</p>
          <p className="mt-1 text-sm text-slate-300">Flashed as a gold-and-red welcome popup on every visit and again every 10 minutes. Several offers rotate automatically. Only one total-price offer (combo or rupees off) applies per order — the biggest saving wins.</p>
        </div>
        <button data-testid="offer-new-btn" onClick={() => setEditing({ ...EMPTY_OFFER })} className="flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-5 py-2.5 text-xs font-bold text-[#0A0B0E]">
          <Plus size={14} /> New offer
        </button>
      </div>

      {editing && (
        <div className="grid gap-4 rounded-2xl border border-white/10 bg-[#12141A] p-5 lg:grid-cols-2" data-testid="offer-editor">
          <div className="space-y-3">
            <input data-testid="offer-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Karwa Chouth Sale" className={inputCls} />
            <textarea data-testid="offer-message-input" value={editing.message} onChange={(e) => setEditing({ ...editing, message: e.target.value })} rows={3} placeholder="15% off all MDF clocks and thali sets" className={inputCls} />
            <input data-testid="offer-cta-input" value={editing.cta_label} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} placeholder="Button text, e.g. Shop now" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Starts</span>
                <input data-testid="offer-start-input" type="date" value={editing.starts_on} onChange={(e) => setEditing({ ...editing, starts_on: e.target.value })} className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Ends</span>
                <input data-testid="offer-end-input" type="date" value={editing.ends_on} onChange={(e) => setEditing({ ...editing, ends_on: e.target.value })} className={`${inputCls} mt-1`} />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input data-testid="offer-active-toggle" type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-cyan-400" />
              Switched on
            </label>

            <div className="rounded-xl border border-[#F5C45A]/30 bg-[#F5C45A]/5 p-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F5C45A]">Cut prices during this offer</p>
              <select
                data-testid="offer-deal-type"
                value={editing.deal_type}
                onChange={(e) => setEditing({ ...editing, deal_type: e.target.value })}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-3 py-2 text-sm text-slate-300 focus:border-cyan-400/50 focus:outline-none"
              >
                {Object.entries(DEAL_TYPES).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>

              {(editing.deal_type === "item_percent" || editing.deal_type === "cart_percent") && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    data-testid="offer-discount-input"
                    type="number"
                    min={0}
                    max={90}
                    value={editing.discount_percent}
                    onChange={(e) => setEditing({ ...editing, discount_percent: Number(e.target.value) })}
                    className="w-24 rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-3 py-2 text-sm focus:border-cyan-400/50 focus:outline-none"
                  />
                  <span className="text-xs text-slate-400">
                    {editing.deal_type === "item_percent" ? "% off each product you tick below" : "% off the combined total of the ticked products"}
                  </span>
                </div>
              )}

              {editing.deal_type === "cart_amount" && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">Rupees off</span>
                    <input data-testid="offer-amount-input" type="number" min={0} value={editing.cart_discount_amount} onChange={(e) => setEditing({ ...editing, cart_discount_amount: Number(e.target.value) })} className="mt-1 w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-3 py-2 text-sm focus:border-cyan-400/50 focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">When listed items reach</span>
                    <input data-testid="offer-min-total-input" type="number" min={0} value={editing.cart_min_total} onChange={(e) => setEditing({ ...editing, cart_min_total: Number(e.target.value) })} className="mt-1 w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-3 py-2 text-sm focus:border-cyan-400/50 focus:outline-none" />
                  </label>
                </div>
              )}

              {editing.deal_type === "bundle" && (
                <div className="mt-2">
                  <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">Combo price for the whole set</span>
                    <input data-testid="offer-bundle-price-input" type="number" min={0} value={editing.bundle_price} onChange={(e) => setEditing({ ...editing, bundle_price: Number(e.target.value) })} className="mt-1 w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-3 py-2 text-sm focus:border-cyan-400/50 focus:outline-none" />
                  </label>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400">
                    The customer must take every ticked product (in the quantity you set) to get this price. It also appears as a "Combo deal" card in the catalogue.
                  </p>
                </div>
              )}
              <input
                data-testid="offer-product-search"
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                placeholder="Find a product by name or code..."
                className="mt-3 w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-3 py-2 text-xs placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
              />
              <div className="mt-2 max-h-44 space-y-1 overflow-y-auto" data-testid="offer-product-list">
                {(products || [])
                  .filter((pr) => {
                    const t = productQuery.trim().toLowerCase();
                    return !t || `${pr.sku || ""} ${pr.name}`.toLowerCase().includes(t);
                  })
                  .slice(0, 80)
                  .map((pr) => (
                    <label key={pr.id} data-testid={`offer-product-${pr.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-xs text-slate-300 hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={editing.product_ids.includes(pr.id)}
                        onChange={() =>
                          setEditing((f) => ({
                            ...f,
                            product_ids: f.product_ids.includes(pr.id) ? f.product_ids.filter((x) => x !== pr.id) : [...f.product_ids, pr.id],
                          }))
                        }
                        className="accent-[#F5C45A]"
                      />
                      <span className="truncate">{pr.sku ? <span className="font-mono text-cyan-300">{pr.sku} </span> : null}{pr.name}</span>
                      {editing.deal_type === "bundle" && editing.product_ids.includes(pr.id) && (
                        <input
                          data-testid={`offer-qty-${pr.id}`}
                          type="number"
                          min={1}
                          value={editing.bundle_qty?.[pr.id] || 1}
                          onClick={(e) => e.preventDefault()}
                          onChange={(e) => setEditing((f) => ({ ...f, bundle_qty: { ...f.bundle_qty, [pr.id]: Math.max(1, Number(e.target.value)) } }))}
                          className="w-14 shrink-0 rounded-lg border border-white/10 bg-[#0A0B0E]/60 px-2 py-1 text-[11px] focus:border-cyan-400/50 focus:outline-none"
                        />
                      )}
                      <span className="ml-auto shrink-0 font-mono text-[10px] text-slate-500">
                        {formatINR(pr.price)}
                        {editing.deal_type === "item_percent" && editing.discount_percent > 0 && editing.product_ids.includes(pr.id) && (
                          <span className="ml-1 text-[#F5C45A]">→ {formatINR(Math.round((pr.price * (100 - editing.discount_percent)) / 100))}</span>
                        )}
                      </span>
                    </label>
                  ))}
              </div>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500" data-testid="offer-selected-count">
                {editing.product_ids.length} product(s) selected
                {editing.deal_type === "bundle" && editing.product_ids.length > 0 && (() => {
                  const full = editing.product_ids.reduce((sum, id) => {
                    const pr = (products || []).find((x) => x.id === id);
                    const unit = pr ? (pr.offer_price != null ? pr.offer_price : pr.price) : 0;
                    return sum + unit * Math.max(1, editing.bundle_qty?.[id] || 1);
                  }, 0);
                  return <span className="ml-1 text-[#F5C45A]">· normally {formatINR(full)} → combo {formatINR(editing.bundle_price)}</span>;
                })()}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="offer-link-type" value={editing.link_type} onChange={(e) => setEditing({ ...editing, link_type: e.target.value, link_value: "" })} className={inputCls}>
                <option value="none">Just scroll to catalogue</option>
                <option value="collection">Open a collection</option>
                <option value="category">Open a category</option>
              </select>
              {editing.link_type !== "none" && (
                <select data-testid="offer-link-value" value={editing.link_value} onChange={(e) => setEditing({ ...editing, link_value: e.target.value })} className={inputCls}>
                  <option value="">Select…</option>
                  {(editing.link_type === "collection"
                    ? collections.filter((c) => c.product_count > 0).map((c) => c.name)
                    : (categories || []).filter((c) => c.product_count > 0).map((c) => c.name)
                  ).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="rounded-xl border border-white/10 p-3">
              {editing.image ? (
                <img src={editing.image} alt="Offer" data-testid="offer-image-preview" className="h-40 w-full rounded-lg object-cover" />
              ) : (
                <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-white/15 text-xs text-slate-600">No image yet</div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" />
              <div className="mt-3 flex gap-2">
                <button data-testid="offer-upload-btn" onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300">
                  <Upload size={13} /> Upload image
                </button>
                {editing.image && (
                  <button data-testid="offer-remove-image-btn" onClick={() => setEditing({ ...editing, image: "" })} className="rounded-full border border-white/15 px-4 py-2 text-xs text-slate-400 hover:border-red-400/40 hover:text-red-400">
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button data-testid="offer-save-btn" onClick={save} disabled={saving} className="flex-1 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-50">
                {saving ? "Saving..." : "Save offer"}
              </button>
              <button data-testid="offer-cancel-btn" onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2" data-testid="offers-list">
        {list.map((o) => (
          <div key={o.id} data-testid={`offer-card-${o.id}`} className="overflow-hidden rounded-2xl border border-white/10 bg-[#12141A]">
            {o.image && <img src={o.image} alt={o.title} className="h-32 w-full object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-bold text-slate-100">{o.title}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{o.message || "—"}</p>
                </div>
                <button
                  data-testid={`offer-toggle-${o.id}`}
                  onClick={() => toggle(o)}
                  className={`shrink-0 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${badge(o).cls}`}
                >
                  {badge(o).label}
                </button>
              </div>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                {o.starts_on || "Now"} → {o.ends_on || "No end date"}{o.link_value ? ` · opens ${o.link_value}` : ""}
                {o.deal_type === "bundle"
                  ? ` · combo of ${o.product_ids.reduce((n, id) => n + Math.max(1, o.bundle_qty?.[id] || 1), 0)} pc at ${formatINR(o.bundle_price)}`
                  : o.deal_type === "cart_amount"
                  ? ` · ${formatINR(o.cart_discount_amount)} off above ${formatINR(o.cart_min_total)}`
                  : o.discount_percent > 0
                  ? ` · ${Math.round(o.discount_percent)}% off ${o.product_ids.length} product(s)${o.deal_type === "cart_percent" ? " (on total)" : ""}`
                  : ""}
              </p>
              <div className="mt-3 flex gap-2">
                <button data-testid={`offer-edit-${o.id}`} onClick={() => setEditing(o)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"><Pencil size={14} /></button>
                <button data-testid={`offer-delete-${o.id}`} onClick={() => remove(o)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-red-400/40 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {!list.length && (
          <p className="rounded-2xl border border-white/10 bg-[#12141A] px-5 py-10 text-center text-sm text-slate-500 md:col-span-2" data-testid="offers-empty">
            No offers yet — tap "New offer" to flash your first festive deal.
          </p>
        )}
      </div>
    </div>
  );
}

function CollectionsPanel({ products, onChanged }) {
  const EMPTY = { name: "", tagline: "", banner: "", product_ids: [], active: true };
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const bannerRef = useRef(null);

  const load = useCallback(() => {
    api.get("/collections?all=true").then(({ data }) => setList(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "banner");
      const { data } = await api.post("/admin/upload-image", fd);
      setEditing((f) => ({ ...f, banner: data.url }));
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    if (!editing.name.trim()) {
      toast.error("Give the collection a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        tagline: editing.tagline.trim(),
        banner: editing.banner,
        product_ids: editing.product_ids,
        active: editing.active,
      };
      if (editing.id) await api.put(`/admin/collections/${editing.id}`, payload);
      else await api.post("/admin/collections", payload);
      toast.success(`${payload.name} saved`);
      setEditing(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the ${c.name} collection? Products stay in the catalogue.`)) return;
    try {
      await api.delete(`/admin/collections/${c.id}`);
      toast.success("Collection deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleProduct = (id) =>
    setEditing((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id],
    }));

  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E] px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";
  const matches = products.filter((p) =>
    `${p.name} ${p.sku || ""} ${p.category}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mt-5 space-y-4" data-testid="admin-collections-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Festive sets with their own banner — they appear above the catalogue
        </p>
        <button
          data-testid="admin-add-collection-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="flex items-center gap-2 rounded-full border border-fuchsia-400/40 px-5 py-2 text-sm font-semibold text-fuchsia-300 hover:bg-fuchsia-400/10"
        >
          <Plus size={15} /> New Collection
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-collections-list">
        {list.map((c) => (
          <div key={c.id} data-testid={`admin-collection-${c.slug}`} className="overflow-hidden rounded-2xl border border-white/10 bg-[#12141A]">
            <div className="relative h-28 bg-gradient-to-br from-fuchsia-500/20 to-cyan-400/10">
              {c.banner && <img src={c.banner} alt="" className="h-full w-full object-cover" />}
              {!c.active && (
                <span className="absolute left-3 top-3 rounded-full bg-[#0A0B0E]/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-yellow-300">Hidden</span>
              )}
            </div>
            <div className="p-4">
              <p className="font-display text-base font-bold">{c.name}</p>
              <p className="mt-0.5 truncate text-xs text-slate-500">{c.tagline || "No tagline"}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-300">{c.product_count} blanks</p>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid={`admin-edit-collection-${c.slug}`}
                  onClick={() => setEditing({ ...c })}
                  className="flex-1 rounded-lg border border-white/10 py-2 text-xs font-semibold text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300"
                >
                  Edit
                </button>
                <button
                  data-testid={`admin-delete-collection-${c.slug}`}
                  onClick={() => remove(c)}
                  className="rounded-lg border border-white/10 px-3 py-2 text-slate-500 hover:border-red-400/40 hover:text-red-400"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {!list.length && (
          <p data-testid="admin-collections-empty" className="rounded-2xl border border-white/10 bg-[#12141A] p-8 text-center text-sm text-slate-500 sm:col-span-2 lg:col-span-3">
            No collections yet — create one for Karwa Chouth, Diwali or Raksha Bandhan.
          </p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="collection-editor">
          <div className="my-6 w-full max-w-2xl rounded-3xl border border-white/10 bg-[#12141A] p-5 sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-xl font-bold">{editing.id ? "Edit collection" : "New collection"}</h3>
              <button data-testid="collection-editor-close" onClick={() => setEditing(null)} className="text-slate-500 hover:text-red-400">
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Name</span>
                <input data-testid="collection-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} placeholder="Karwa Chouth" />
              </label>
              <label className="block">
                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Tagline</span>
                <input data-testid="collection-tagline-input" value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} className={inputCls} placeholder="Thali sets, gifts & frames" />
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                data-testid="collection-banner-upload-btn"
                onClick={() => bannerRef.current?.click()}
                className="flex items-center gap-2 rounded-full border border-cyan-400/40 px-5 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-400/10"
              >
                <ImageIcon size={15} /> {editing.banner ? "Change banner" : "Upload banner"}
              </button>
              <input ref={bannerRef} type="file" accept="image/*" hidden onChange={uploadBanner} data-testid="collection-banner-input" />
              {editing.banner && <img src={editing.banner} alt="" className="h-14 w-28 rounded-xl border border-white/10 object-cover" data-testid="collection-banner-preview" />}
              <label className="ml-auto flex items-center gap-2 text-sm text-slate-300">
                <input data-testid="collection-active-toggle" type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Show on site
              </label>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Products in this set</span>
                <span className="font-mono text-[11px] text-cyan-300" data-testid="collection-selected-count">{editing.product_ids.length} selected</span>
              </div>
              <input
                data-testid="collection-product-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${inputCls} mt-2`}
                placeholder="Search blanks by name or code..."
              />
              <div className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-white/10 p-2" data-testid="collection-product-list">
                {matches.slice(0, 120).map((p) => (
                  <label key={p.id} data-testid={`collection-product-${p.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-white/[0.04]">
                    <input type="checkbox" checked={editing.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-300">{p.name}</span>
                    <span className="shrink-0 font-mono text-[10px] uppercase text-slate-500">{p.sku}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-full border border-white/10 px-5 py-2.5 text-sm text-slate-300">Cancel</button>
              <button
                data-testid="collection-save-btn"
                onClick={save}
                disabled={saving}
                className="rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-6 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save collection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomersPanel({ role = "admin", states = [] }) {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [activityFor, setActivityFor] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [groupFilter, setGroupFilter] = useState("");
  const { groups, reloadGroups } = useCustomerGroups();
  const isStaff = role === "staff";

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/customers");
    setCustomers(data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  const saveNote = async (customer, note) => {
    try {
      const { data } = await api.patch(`/admin/customers/${customer.id}/note`, { note });
      setCustomers((cur) => cur.map((c) => (c.id === customer.id ? { ...c, note: data.note, note_by: data.note_by } : c)));
      setNoteFor(null);
      toast.success("Note saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.full_name} (${c.phone})?`)) return;
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success("Customer removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Full name", "Company", "Mobile", "City", "State", "Registered", "Last login", "Note"],
      ...customers.map((c) => [
        c.full_name, c.company_name || "", c.phone, c.city || "", c.state,
        c.created_at ? new Date(c.created_at).toLocaleString() : "",
        c.last_login_at ? new Date(c.last_login_at).toLocaleString() : "",
        c.note || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `varsha-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`${customers.length} customers exported`);
  };

  const visible = customers
    .filter((c) =>
      `${c.full_name} ${c.company_name} ${c.phone} ${c.city} ${c.state}`.toLowerCase().includes(query.toLowerCase())
    )
    .filter((c) => !groupFilter || groupFilter.startsWith("auto:") || (c.groups || []).includes(groupFilter))
    .sort((a, b) => new Date(b.last_login_at || b.created_at || 0) - new Date(a.last_login_at || a.created_at || 0));

  return (
    <div className="mt-5 space-y-4" data-testid="admin-customers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <div>
          <p className="font-display text-2xl font-bold" data-testid="admin-customers-count">{customers.length}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Registered customers · most recently active first</p>
          {isStaff && (
            <p className="mt-2 max-w-md text-xs text-cyan-300" data-testid="staff-scope-note">
              You are seeing customers of {states.length ? states.join(", ") : "no state yet"}.
            </p>
          )}
        </div>
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <input
            data-testid="admin-customer-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, company, number, city..."
            className="w-full max-w-xs rounded-full border border-white/10 bg-[#0A0B0E]/60 px-4 py-2 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
          />
          <button data-testid="admin-export-customers-btn" onClick={exportCsv} className="rounded-full border border-cyan-400/40 px-5 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-400/10">
            Export CSV
          </button>
        </div>
      </div>
      <CustomerGroupsBar groups={groups} reloadGroups={reloadGroups} onPick={setGroupFilter} active={groupFilter} />
      <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-customers-table">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-[#12141A] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-3.5">Customer</th>
              <th className="px-5 py-3.5">Mobile</th>
              <th className="px-5 py-3.5">City / State</th>
              <th className="px-5 py-3.5">Registered</th>
              <th className="px-5 py-3.5">Last login</th>
              <th className="px-5 py-3.5">Note</th>
              <th className="px-5 py-3.5">Groups</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.id} data-testid={`admin-customer-row-${c.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-5 py-3">
                  <div className="font-medium text-slate-200">{c.full_name}</div>
                  {c.company_name && <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{c.company_name}</div>}
                </td>
                <td className="px-5 py-3 font-mono text-cyan-300">{c.phone}</td>
                <td className="px-5 py-3 text-slate-400">{c.city ? `${c.city}, ` : ""}{c.state}</td>
                <td className="px-5 py-3 font-mono text-[11px] text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-3 font-mono text-[11px]" data-testid={`admin-customer-last-login-${c.id}`}>
                  {c.last_login_at ? (
                    <>
                      <span className="text-cyan-300">{timeAgo(c.last_login_at)}</span>
                      <span className="block text-slate-600">{new Date(c.last_login_at).toLocaleString()}</span>
                    </>
                  ) : (
                    <span className="text-slate-600">Never</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <button
                    data-testid={`admin-customer-note-${c.id}`}
                    onClick={() => setNoteFor(c)}
                    title={c.note ? "Edit follow-up note" : "Add a follow-up note"}
                    className={`max-w-[200px] truncate rounded-lg border px-3 py-1.5 text-left text-xs ${c.note ? "border-cyan-400/30 bg-cyan-400/5 text-slate-300" : "border-white/10 text-slate-500 hover:border-cyan-400/40 hover:text-cyan-300"}`}
                  >
                    {c.note || "Add note"}
                  </button>
                  {c.note_by && <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-600">by {c.note_by}</div>}
                </td>
                <td className="px-5 py-3">
                  <CustomerGroupPicker
                    customer={c}
                    groups={groups}
                    onSaved={(id, next) => {
                      setCustomers((cur) => cur.map((x) => (x.id === id ? { ...x, groups: next } : x)));
                      reloadGroups();
                    }}
                  />
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      data-testid={`admin-customer-activity-${c.id}`}
                      onClick={() => setActivityFor(c)}
                      title="What they searched and viewed"
                      className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"
                    >
                      <Activity size={14} />
                    </button>
                    {c.phone && (
                      <a
                        data-testid={`admin-whatsapp-customer-${c.id}`}
                        href={`https://wa.me/${c.phone.replace(/\D/g, "").replace(/^(?!91)/, "91")}?text=${encodeURIComponent(`Hello ${c.full_name || ""}, this is ${BRAND}. Here is our latest catalogue and offers: ${window.location.origin}`)}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Message this customer on WhatsApp"
                        className="rounded-lg border border-emerald-400/30 p-2 text-emerald-400 transition-colors hover:bg-emerald-400/10"
                      >
                        <MessageCircle size={14} />
                      </a>
                    )}
                    {!isStaff && (
                      <button data-testid={`admin-delete-customer-${c.id}`} onClick={() => remove(c)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-red-400/40 hover:text-red-400"><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!visible.length && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-500" data-testid="admin-customers-empty">No customers yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {activityFor && <CustomerActivityModal customer={activityFor} onClose={() => setActivityFor(null)} />}
      {noteFor && <CustomerNoteModal customer={noteFor} onSave={saveNote} onClose={() => setNoteFor(null)} />}
    </div>
  );
}

function CustomerNoteModal({ customer, onSave, onClose }) {
  const [note, setNote] = useState(customer.note || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(customer, note);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="customer-note-modal">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-white/10 bg-[#12141A] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold">{customer.full_name}</h3>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{customer.phone} · {customer.state}</p>
          </div>
          <button type="button" data-testid="customer-note-close" onClick={onClose} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:text-red-400"><X size={14} /></button>
        </div>
        <textarea
          data-testid="customer-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={5}
          maxLength={1000}
          placeholder="Follow-up note — what they asked for, when to call back..."
          className="mt-4 w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
        />
        <button
          data-testid="customer-note-save-btn"
          type="submit"
          disabled={saving}
          className="mt-4 w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3 text-sm font-bold text-[#0A0B0E] disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save note"}
        </button>
      </form>
    </div>
  );
}

const EMPTY_STAFF = { name: "", phone: "", password: "", assigned_states: [], active: true };

function StaffPanel() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(EMPTY_STAFF);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/staff");
    setStaff(data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  const toggleState = (s) =>
    setForm((f) => ({
      ...f,
      assigned_states: f.assigned_states.includes(s) ? f.assigned_states.filter((x) => x !== s) : [...f.assigned_states, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/staff/${editingId}`, form);
        toast.success(`${form.name} updated`);
      } else {
        await api.post("/admin/staff", form);
        toast.success(`${form.name} can now log in with ${form.phone}`);
      }
      setForm(EMPTY_STAFF);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, phone: s.phone, password: "", assigned_states: s.assigned_states || [], active: s.active });
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/staff/${s.id}`, { ...s, password: "", active: !s.active });
      toast.success(`${s.name} ${s.active ? "switched off" : "switched on"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}'s access?`)) return;
    try {
      await api.delete(`/admin/staff/${s.id}`);
      toast.success("Staff access removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-5 grid gap-4 lg:grid-cols-[380px_1fr]" data-testid="admin-staff-panel">
      <form onSubmit={submit} className="space-y-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{editingId ? "Edit staff member" : "Add staff member"}</p>
        <input data-testid="staff-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Staff name" className={inputCls} />
        <input data-testid="staff-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Login mobile number" className={inputCls} />
        <input
          data-testid="staff-password-input"
          type="text"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder={editingId ? "New password (leave blank to keep)" : "Password (min 8 characters)"}
          className={inputCls}
        />
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Assigned states ({form.assigned_states.length})</p>
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-white/10 p-2" data-testid="staff-states-list">
            {INDIAN_STATES.map((s) => (
              <label key={s} data-testid={`staff-state-${s.replace(/\s+/g, "-").toLowerCase()}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm text-slate-300 hover:bg-white/5">
                <input type="checkbox" checked={form.assigned_states.includes(s)} onChange={() => toggleState(s)} className="accent-cyan-400" />
                {s}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input data-testid="staff-active-toggle" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-cyan-400" />
          Access switched on
        </label>
        <div className="flex gap-2">
          <button data-testid="staff-save-btn" type="submit" disabled={busy || !form.assigned_states.length} className="flex-1 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-50">
            {busy ? "Saving..." : editingId ? "Save changes" : "Add staff"}
          </button>
          {editingId && (
            <button data-testid="staff-cancel-btn" type="button" onClick={() => { setEditingId(null); setForm(EMPTY_STAFF); }} className="rounded-full border border-white/15 px-5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
              Cancel
            </button>
          )}
        </div>
        <p className="text-xs leading-relaxed text-slate-500">
          Staff log in at this same screen with their mobile number and password. They only see customers and orders of the states you tick — nothing else.
        </p>
      </form>

      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[560px] text-left text-sm" data-testid="admin-staff-table">
          <thead>
            <tr className="border-b border-white/10 bg-[#12141A] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-3.5">Staff</th>
              <th className="px-5 py-3.5">States</th>
              <th className="px-5 py-3.5">Access</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} data-testid={`admin-staff-row-${s.id}`} className="border-b border-white/5">
                <td className="px-5 py-3">
                  <div className="font-medium text-slate-200">{s.name}</div>
                  <div className="font-mono text-xs text-cyan-300">{s.phone}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-600">
                    {s.last_login_at ? `Last login ${timeAgo(s.last_login_at)}` : "Never logged in"}
                  </div>
                </td>
                <td className="px-5 py-3 text-xs text-slate-400">{(s.assigned_states || []).join(", ") || "—"}</td>
                <td className="px-5 py-3">
                  <button
                    data-testid={`admin-staff-toggle-${s.id}`}
                    onClick={() => toggleActive(s)}
                    className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${s.active ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10" : "border-red-400/40 text-red-300 hover:bg-red-400/10"}`}
                  >
                    {s.active ? "On" : "Off"}
                  </button>
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`admin-staff-edit-${s.id}`} onClick={() => edit(s)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"><Pencil size={14} /></button>
                    <button data-testid={`admin-staff-delete-${s.id}`} onClick={() => remove(s)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-red-400/40 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!staff.length && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-slate-500" data-testid="admin-staff-empty">No staff yet — add one on the left.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoriesPanel({ categories, onChanged }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/categories", { name });
      toast.success(`Category "${name.trim()}" added`);
      setName("");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (cat) => {
    const next = window.prompt("Rename category — products move with it", cat.name);
    if (!next || next.trim() === cat.name) return;
    try {
      const { data } = await api.put(`/admin/categories/${cat.id}`, { name: next });
      toast.success(`Renamed — ${data.products_updated} products moved`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${cat.id}`);
      toast.success("Category deleted");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-5 space-y-4" data-testid="admin-categories-panel">
      <form onSubmit={add} className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#12141A] p-5 sm:flex-row sm:items-center">
        <input data-testid="new-category-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name e.g. Photo Slates" className={inputCls} />
        <button data-testid="add-category-btn" type="submit" disabled={busy} className="shrink-0 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-6 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-50">
          {busy ? "Adding..." : "Add Category"}
        </button>
      </form>
      <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="admin-categories-table">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-[#12141A] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-3.5">Category</th>
              <th className="px-5 py-3.5">Products</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.id} data-testid={`admin-category-row-${c.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-5 py-3 font-medium text-slate-200">{c.name}</td>
                <td className="px-5 py-3 font-mono text-cyan-300" data-testid={`admin-category-count-${c.id}`}>{c.product_count}</td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`admin-rename-category-${c.id}`} onClick={() => rename(c)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"><Pencil size={14} /></button>
                    <button data-testid={`admin-delete-category-${c.id}`} onClick={() => remove(c)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-red-400/40 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RouteCard({ route, onSave, onDelete }) {
  const [label, setLabel] = useState(route.label || "");
  const [number, setNumber] = useState(route.number || "");
  const [isDefault, setIsDefault] = useState(!!route.is_default);
  const [states, setStates] = useState(route.states || []);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(!route.id);

  const toggleState = (s) => setStates((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ label: label.trim() || "Sales desk", number, states, is_default: isDefault });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";
  const testId = route.id || "new";

  return (
    <div data-testid={`whatsapp-route-${testId}`} className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <input data-testid={`route-label-${testId}`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Desk name e.g. North India" className={inputCls} />
        <input data-testid={`route-number-${testId}`} value={number} onChange={(e) => setNumber(e.target.value)} placeholder="WhatsApp number with country code e.g. 919760877279" className={inputCls} />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-400">
          <input data-testid={`route-default-${testId}`} type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="h-4 w-4 accent-cyan-400" />
          Default desk (used for states not assigned below)
        </label>
        <button data-testid={`route-toggle-states-${testId}`} onClick={() => setOpen((o) => !o)} className="font-mono text-[11px] uppercase tracking-[0.15em] text-cyan-300 hover:text-cyan-200">
          {open ? "Hide states" : `${states.length} state${states.length === 1 ? "" : "s"} assigned`}
        </button>
      </div>
      {open && (
        <div data-testid={`route-states-${testId}`} className="mt-4 grid max-h-64 grid-cols-2 gap-2 overflow-y-auto rounded-xl border border-white/10 bg-[#0A0B0E]/50 p-3 sm:grid-cols-3">
          {INDIAN_STATES.map((s) => (
            <label key={s} className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                data-testid={`route-state-${testId}-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                checked={states.includes(s)}
                onChange={() => toggleState(s)}
                className="h-3.5 w-3.5 accent-fuchsia-400"
              />
              {s}
            </label>
          ))}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <button data-testid={`route-save-${testId}`} onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-6 py-2 text-sm font-bold text-[#0A0B0E] disabled:opacity-50">
          {saving ? "Saving..." : "Save desk"}
        </button>
        {route.id ? (
          <button data-testid={`route-delete-${route.id}`} onClick={() => onDelete(route.id)} className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-400 hover:border-red-400/40 hover:text-red-400">
            Delete
          </button>
        ) : (
          <button data-testid="route-cancel-new" onClick={() => onDelete(null)} className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-400 hover:text-slate-200">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function WhatsAppRoutes() {
  const [routes, setRoutes] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/whatsapp-routes");
    setRoutes(data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  const save = async (id, payload) => {
    try {
      if (id) await api.put(`/admin/whatsapp-routes/${id}`, payload);
      else await api.post("/admin/whatsapp-routes", payload);
      toast.success("WhatsApp desk saved");
      setAdding(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this WhatsApp desk?")) return;
    try {
      await api.delete(`/admin/whatsapp-routes/${id}`);
      toast.success("Desk deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const assigned = routes.reduce((n, r) => n + (r.states?.length || 0), 0);

  return (
    <div className="mt-5 space-y-4" data-testid="admin-whatsapp-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <p className="max-w-xl text-sm text-slate-400">
          Assign Indian states to each WhatsApp number. Orders from a customer's state open on that desk's WhatsApp;
          anything unassigned goes to the default desk. <span className="text-cyan-300" data-testid="whatsapp-assigned-count">{assigned} of {INDIAN_STATES.length} states assigned</span>.
        </p>
        <button
          data-testid="admin-add-route-btn"
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 rounded-full border border-cyan-400/40 px-5 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-400/10"
        >
          <Plus size={15} /> New WhatsApp Desk
        </button>
      </div>
      {adding && <RouteCard route={{ states: [] }} onSave={(p) => save(null, p)} onDelete={() => setAdding(false)} />}
      {routes.map((r) => (
        <RouteCard key={r.id} route={r} onSave={(p) => save(r.id, p)} onDelete={remove} />
      ))}
    </div>
  );
}

const ORDER_STAGE_LIST = ["placed", "discussion", "pending", "confirmed", "packed", "dispatched", "delivered", "cancelled"];

const STAGE_COLORS = {
  placed: "text-cyan-300 border-cyan-400/40",
  discussion: "text-violet-300 border-violet-400/40",
  pending: "text-orange-300 border-orange-400/40",
  confirmed: "text-sky-300 border-sky-300/40",
  packed: "text-yellow-300 border-yellow-300/40",
  dispatched: "text-fuchsia-300 border-fuchsia-400/40",
  delivered: "text-emerald-400 border-emerald-400/40",
  cancelled: "text-red-400 border-red-400/40",
};

function orderUpdateMessage(order, stage) {
  const ref = `#${String(order.id).slice(-6).toUpperCase()}`;
  const items = (order.items || []).map((i) => `• ${i.name}${i.sku ? ` [${i.sku}]` : ""} x${i.qty}${itemDetailsText(i) ? `\n${itemDetailsText(i)}` : ""}`).join("\n");
  const head = `Hello ${order.customer_name || ""}, ${BRAND} here.\nOrder ${ref}\n${items}`;
  if (stage === "dispatched" || stage === "delivered") {
    return `${head}\n\nYour order has been ${stage}.\nCourier: ${order.courier_name || "-"}\nAWB: ${order.tracking_number || "-"}\nTrack here: ${order.tracking_url || "-"}`;
  }
  if (stage === "cancelled") return `${head}\n\nYour order has been cancelled. Please reply if this is unexpected.`;
  const note = { placed: "We have received your order.", confirmed: "Your order is confirmed and in production.", packed: "Your order is packed and ready to dispatch.", discussion: "We would like to discuss a few details of your order.", pending: "Your order is on hold pending confirmation." }[stage] || "";
  return `${head}\n\n${note}`;
}

function OrderStageEditor({ order, onSaved }) {
  const [stage, setStage] = useState(order.stage || "placed");
  const [courier, setCourier] = useState(order.courier_name || "");
  const [awb, setAwb] = useState(order.tracking_number || "");
  const [url, setUrl] = useState(order.tracking_url || "");
  const [saving, setSaving] = useState(false);
  const needsCourier = stage === "dispatched" || stage === "delivered";

  const save = async (nextStage = stage) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/stage`, {
        stage: nextStage,
        courier_name: courier.trim(),
        tracking_number: awb.trim(),
        tracking_url: url.trim(),
      });
      toast.success(`Order marked ${nextStage}`);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-[#0A0B0E]/60 p-3" data-testid={`admin-order-stage-box-${order.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Stage</span>
        <select
          data-testid={`admin-order-stage-${order.id}`}
          value={stage}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "cancelled" && !window.confirm("Mark this order cancelled? The customer will see it as cancelled.")) return;
            setStage(next);
            if (next !== "dispatched" && next !== "delivered") save(next);
          }}
          className={`rounded-full border bg-[#0A0B0E] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] focus:outline-none ${STAGE_COLORS[stage] || ""}`}
        >
          {ORDER_STAGE_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {order.tracking_url && (
          <a
            href={order.tracking_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`admin-order-track-link-${order.id}`}
            className="font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-300 hover:underline"
          >
            Open tracking
          </a>
        )}
        {order.customer_phone && (
          <a
            data-testid={`admin-order-whatsapp-${order.id}`}
            href={`https://wa.me/${order.customer_phone.replace(/\D/g, "").replace(/^(?!91)/, "91")}?text=${encodeURIComponent(orderUpdateMessage(order, stage))}`}
            target="_blank"
            rel="noreferrer"
            className="ml-auto flex items-center gap-1.5 rounded-full border border-emerald-400/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400 transition-colors hover:bg-emerald-400/10"
          >
            <MessageCircle size={12} /> WhatsApp update
          </a>
        )}
      </div>
      {needsCourier && (
        <div className="mt-3 space-y-2" data-testid={`admin-order-courier-form-${order.id}`}>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              data-testid={`admin-order-courier-name-${order.id}`}
              value={courier}
              onChange={(e) => setCourier(e.target.value)}
              placeholder="Courier name (e.g. Delhivery)"
              className="rounded-lg border border-white/10 bg-[#12141A] px-3 py-2 text-xs placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
            />
            <input
              data-testid={`admin-order-awb-${order.id}`}
              value={awb}
              onChange={(e) => setAwb(e.target.value)}
              placeholder="Tracking / AWB number"
              className="rounded-lg border border-white/10 bg-[#12141A] px-3 py-2 font-mono text-xs placeholder:font-sans placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
            />
          </div>
          <input
            data-testid={`admin-order-tracking-url-${order.id}`}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://courier-site.com/track?awb=..."
            className="w-full rounded-lg border border-white/10 bg-[#12141A] px-3 py-2 font-mono text-xs placeholder:font-sans placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
          />
          <button
            data-testid={`admin-order-save-tracking-${order.id}`}
            onClick={() => save(stage)}
            disabled={saving}
            className="rounded-full border border-cyan-400/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-300 transition-colors hover:bg-cyan-400/10 disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save tracking link"}
          </button>
          <p className="font-mono text-[10px] text-slate-600">Customers see this link on their Track Order button.</p>
        </div>
      )}
    </div>
  );
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#12141A] p-8"
      data-testid="admin-login-card"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-fuchsia-400">Restricted</div>
      <h1 className="mt-2 font-display text-2xl font-bold">Admin Access</h1>
      <p className="mt-1 text-xs text-slate-500">Owner: email + password. Staff: mobile number + password.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <input
          data-testid="admin-email-input"
          type="text"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email or mobile number"
          className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
        />
        <input
          data-testid="admin-passcode-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-fuchsia-400/50 focus:outline-none"
        />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
        <button
          data-testid="admin-login-submit-btn"
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3 text-sm font-bold text-[#0A0B0E] transition-transform hover:scale-[1.02] disabled:opacity-50"
        >
          {loading ? "Verifying..." : "Enter Dashboard"}
        </button>
      </form>
    </motion.div>
  );
}

function ProductForm({ initial, categories, onSave, onClose }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      sku: form.sku?.trim() || null,
      price: parseFloat(form.price) || 0,
      bulk_price: form.bulk_price ? parseFloat(form.bulk_price) : null,
      bulk_min: form.bulk_min ? parseInt(form.bulk_min) : null,
      stock: parseInt(form.stock) || 0,
      substrate: form.substrate.split(",").map((s) => s.trim()).filter(Boolean),
    });
  };

  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0B0E]/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="product-form-modal"
    >
      <motion.form
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/10 bg-[#12141A] p-7"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">{initial.name ? "Edit Blank" : "New Blank"}</h3>
          <button type="button" data-testid="product-form-close-btn" onClick={onClose} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-fuchsia-300"><X size={15} /></button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <input data-testid="product-form-name" required value={form.name} onChange={set("name")} placeholder="Product name" className={inputCls} />
          <select data-testid="product-form-category" value={form.category} onChange={set("category")} className={inputCls}>
            {(categories.length ? categories.map((c) => c.name) : CATEGORIES.filter((c) => c !== "All Products")).map((c) => <option key={c}>{c}</option>)}
          </select>
          <input data-testid="product-form-price" required type="number" step="0.01" value={form.price} onChange={set("price")} placeholder="Price (₹)" className={inputCls} />
          <input data-testid="product-form-stock" required type="number" value={form.stock} onChange={set("stock")} placeholder="Stock qty" className={inputCls} />
          <input data-testid="product-form-bulk-price" type="number" step="0.01" value={form.bulk_price} onChange={set("bulk_price")} placeholder="Bulk price (₹, optional)" className={inputCls} />
          <input data-testid="product-form-bulk-min" type="number" value={form.bulk_min} onChange={set("bulk_min")} placeholder="Bulk min qty (optional)" className={inputCls} />
          <input data-testid="product-form-sku" value={form.sku || ""} onChange={set("sku")} placeholder="Product code e.g. MM01 (used for bulk photo upload)" className={`${inputCls} sm:col-span-2`} />
          <SizeOptionsField value={form.size_options || []} onChange={(size_options) => setForm((current) => ({ ...current, size_options }))} />
          <GalleryField
            images={form.images || []}
            onChange={(images) => setForm((f) => ({ ...f, images, image: images[0] || "" }))}
            inputCls={inputCls}
          />
          <VideoField
            videos={form.videos || []}
            onChange={(videos) => setForm((f) => ({ ...f, videos }))}
          />
          <input data-testid="product-form-substrate" value={form.substrate} onChange={set("substrate")} placeholder="Substrate tags, comma separated" className={`${inputCls} sm:col-span-2`} />
          <input data-testid="product-form-press-temp" value={form.press_temp} onChange={set("press_temp")} placeholder="Press temp e.g. 385°F (195°C)" className={inputCls} />
          <input data-testid="product-form-press-time" value={form.press_time} onChange={set("press_time")} placeholder="Press time e.g. 180-240 secs" className={inputCls} />
          <input data-testid="product-form-press-pressure" value={form.press_pressure} onChange={set("press_pressure")} placeholder="Pressure e.g. Medium" className={inputCls} />
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input data-testid="product-form-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-cyan-400" /> Featured product
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input data-testid="product-form-is-new-launch" type="checkbox" checked={!!form.is_new_launch} onChange={set("is_new_launch")} className="h-4 w-4 accent-yellow-300" /> New launch (shows in the New Launch tab)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input data-testid="product-form-is-live" type="checkbox" checked={form.is_live !== false} onChange={set("is_live")} className="h-4 w-4 accent-emerald-400" /> Live on website (off = shown as "Coming soon")
          </label>
          <textarea data-testid="product-form-description" value={form.description} onChange={set("description")} placeholder="Description" rows={3} className={`${inputCls} resize-none sm:col-span-2`} />
        </div>
        <button data-testid="product-form-save-btn" type="submit" className="mt-5 w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3 text-sm font-bold text-[#0A0B0E] transition-transform hover:scale-[1.01]">
          Save Blank
        </button>
      </motion.form>
    </motion.div>
  );
}

function CustomerActivityModal({ customer, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/customers/${customer.id}/activity`)
      .then((res) => setData(res.data))
      .catch(() => setData({ searches: [], views: [], total: 0, error: true }));
  }, [customer.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="customer-activity-modal">
      <div className="my-6 w-full max-w-xl rounded-3xl border border-white/10 bg-[#12141A] p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold">{customer.full_name}</h3>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-cyan-300">{customer.phone} · {customer.city || customer.state}</p>
          </div>
          <button data-testid="customer-activity-close" onClick={onClose} className="text-slate-500 hover:text-red-400"><X size={18} /></button>
        </div>

        {!data ? (
          <p className="mt-6 text-sm text-slate-500" data-testid="activity-loading">Loading activity…</p>
        ) : data.error ? (
          <p className="mt-6 text-sm text-red-400" data-testid="activity-error">Could not load this customer's activity. Please retry.</p>
        ) : (
          <div className="mt-5 space-y-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Searched for</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="activity-searches">
                {data.searches.length ? data.searches.map((s) => (
                  <span key={s.value} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300">
                    {s.value}{s.count > 1 && <span className="ml-1.5 text-cyan-300">x{s.count}</span>}
                  </span>
                )) : <span className="text-sm text-slate-600">No searches yet</span>}
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Products they opened</p>
              <div className="mt-2 space-y-1.5" data-testid="activity-views">
                {data.views.length ? data.views.map((v) => (
                  <div key={`${v.sku}-${v.value}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 truncate text-slate-300">{v.value}</span>
                    {v.sku && <span className="shrink-0 font-mono text-[10px] uppercase text-fuchsia-300">{v.sku}</span>}
                    <span className="shrink-0 font-mono text-[10px] text-cyan-300">x{v.count}</span>
                  </div>
                )) : <span className="text-sm text-slate-600">Nothing opened yet</span>}
              </div>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400" data-testid="activity-total">
              {data.total} tracked actions · pushed to Biziverse every morning, whenever it accepts a fresh lead for this number
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function MessagingPanel() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/whatsapp/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/admin/messages").then(({ data }) => setLog(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-5 space-y-4" data-testid="admin-messaging-panel">
      <WhatsAppCampaigns metaStatus={status?.meta} />
      <WhatsAppProfile metaStatus={status?.meta} />
      <WhatsAppInbox metaStatus={status?.meta} />

      <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="message-log">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-[#12141A] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-3.5">When</th>
              <th className="px-5 py-3.5">Type</th>
              <th className="px-5 py-3.5">To</th>
              <th className="px-5 py-3.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {log.map((m) => (
              <tr key={m.id} data-testid={`message-row-${m.id}`} className="border-b border-white/5">
                <td className="px-5 py-3 font-mono text-[11px] text-slate-500">{new Date(m.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 font-mono text-[11px] uppercase text-fuchsia-300">{m.kind}</td>
                <td className="px-5 py-3 font-mono text-cyan-300">{m.to}</td>
                <td className="px-5 py-3">
                  <span className={m.status === "sent" ? "text-green-400" : m.status === "not_configured" ? "text-yellow-300" : "text-red-400"}>
                    {m.status === "not_configured" ? "Saved (keys missing)" : m.status}
                  </span>
                  {m.error && <span className="block text-[11px] text-slate-500">{m.error}</span>}
                </td>
              </tr>
            ))}
            {!log.length && <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-slate-500" data-testid="message-log-empty">No messages yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ADMIN_TABS = ["products", "categories", "collections", "offers", "orders", "customers", "staff", "crm", "messaging", "homepage", "content", "whatsapp"];
const STAFF_TABS = ["customers", "crm", "orders"];

function Dashboard({ user, onLogout }) {
  const isStaff = user?.role === "staff";
  const tabs = isStaff ? STAFF_TABS : ADMIN_TABS;
  const [tab, setTab] = useState(() => {
    const wanted = new URLSearchParams(window.location.search).get("tab");
    const allowed = user?.role === "staff" ? STAFF_TABS : ADMIN_TABS;
    return allowed.includes(wanted) ? wanted : allowed[0];
  });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [allCustomers, setAllCustomers] = useState([]);

  useEffect(() => {
    if (tab !== "messaging") return;
    api.get("/admin/customers").then(({ data }) => setAllCustomers(data)).catch(() => {});
  }, [tab]);

  const load = useCallback(async () => {
    if (isStaff) {
      const [o, s] = await Promise.all([api.get("/orders"), api.get("/admin/stats")]);
      setOrders(o.data);
      setStats(s.data);
      return;
    }
    const [p, o, s, c] = await Promise.all([
      api.get("/products"),
      api.get("/orders"),
      api.get("/admin/stats"),
      api.get("/categories"),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setStats(s.data);
    setCategories(c.data);
  }, [isStaff]);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  const saveProduct = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/products/${editing.id}`, data);
        toast.success("Blank updated");
      } else {
        await api.post("/products", data);
        toast.success("Blank created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleLive = async (p) => {
    const next = p.is_live === false;
    try {
      await api.patch(`/products/${p.id}/live`, { is_live: next });
      setProducts((cur) => cur.map((x) => (x.id === p.id ? { ...x, is_live: next } : x)));
      toast.success(next ? `${p.name} is live` : `${p.name} marked Coming soon`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleNewLaunch = async (p) => {
    const next = !p.is_new_launch;
    try {
      await api.patch(`/products/${p.id}/new-launch`, { is_new_launch: next });
      setProducts((cur) => cur.map((x) => (x.id === p.id ? { ...x, is_new_launch: next } : x)));
      toast.success(next ? `${p.name} added to New Launch` : `${p.name} removed from New Launch`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this blank?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Blank deleted");
    load();
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visibleProducts = categoryFilter === "All" ? products : products.filter((p) => p.category === categoryFilter);

  const statCards = stats
    ? isStaff
      ? [
          { icon: Users, label: "My Customers", value: stats.total_customers, color: "text-cyan-300" },
          { icon: ShoppingBag, label: "My Orders", value: stats.total_orders, color: "text-fuchsia-300" },
          { icon: Sparkles, label: "New Inquiries", value: stats.new_orders, color: "text-yellow-300" },
        ]
      : [
        { icon: Package, label: "Total Blanks", value: stats.total_products, color: "text-cyan-300" },
        { icon: ShoppingBag, label: "Orders Logged", value: stats.total_orders, color: "text-fuchsia-300" },
        { icon: Sparkles, label: "New Inquiries", value: stats.new_orders, color: "text-yellow-300" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#0A0B0E]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0B0E]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <Link to="/" data-testid="admin-back-to-store" className="flex shrink-0 items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-slate-500 hover:text-cyan-300">
              <ArrowLeft size={13} /> Store
            </Link>
            <span className="truncate font-display text-sm font-extrabold sm:text-base">{BRAND} <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-400">/ {isStaff ? "Staff" : "Admin"}</span></span>
          </div>
          <div className="flex shrink-0 items-center gap-4">
            {isStaff ? (
              <span className="hidden font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 sm:inline" data-testid="staff-user-label">{user.name}</span>
            ) : (
              <button data-testid="admin-backup-btn" onClick={() => setBackupOpen(true)} className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-slate-500 hover:text-cyan-300">
                <DatabaseBackup size={13} /> Data
              </button>
            )}
            <button data-testid="admin-change-password-btn" onClick={() => setChangingPassword(true)} className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-slate-500 hover:text-cyan-300">
              <KeyRound size={13} /> Password
            </button>
            <button data-testid="admin-logout-btn" onClick={onLogout} className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-slate-500 hover:text-red-400">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {statCards.map(({ icon: Icon, label, value, color }) => (
            <div key={label} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
              <Icon size={18} className={color} />
              <div className="mt-3 font-display text-3xl font-bold">{value}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="no-scrollbar -mx-2 flex gap-2 overflow-x-auto px-2 pb-1 lg:flex-wrap lg:overflow-visible">
            {tabs.map((t) => (
              <button
                key={t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`h-10 shrink-0 whitespace-nowrap rounded-full px-5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all ${
                  tab === t ? "bg-gradient-to-r from-cyan-400 to-fuchsia-500 font-bold text-[#0A0B0E]" : "border border-white/10 text-slate-400 hover:text-cyan-300"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {tab === "products" && (
            <div className="flex flex-wrap items-start justify-end gap-2">
              <select
                data-testid="admin-category-filter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-full border border-white/10 bg-[#12141A] px-4 py-2 text-sm text-slate-300 focus:border-cyan-400/50 focus:outline-none"
              >
                <option value="All">All categories ({products.length})</option>                {categories.map((c) => <option key={c.id} value={c.name}>{c.name} ({c.product_count})</option>)}
              </select>
              <BulkUpload onDone={load} />
              <QuickAddRows
                categories={categories.length ? categories : CATEGORIES.slice(1).map((c) => ({ name: c }))}
                defaultCategory={categoryFilter !== "All" ? categoryFilter : (categories[0]?.name || EMPTY_FORM.category)}
                onDone={load}
              />
              <button
                data-testid="admin-add-product-btn"
                onClick={() => setEditing(EMPTY_FORM)}
                className="flex items-center gap-2 rounded-full border border-cyan-400/40 px-5 py-2 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
              >
                <Plus size={15} /> New Blank
              </button>
            </div>
          )}
        </div>
        {tab === "products" && (
          <p className="mt-3 font-mono text-[11px] text-slate-500" data-testid="bulk-upload-hint">
            Bulk upload: name each photo with the product code (e.g. <span className="text-cyan-300">MM01.jpg</span>) — for multiple photos of one product use <span className="text-cyan-300">MM01-1.jpg</span>, <span className="text-cyan-300">MM01-2.jpg</span>. Codes are shown in the table below.
          </p>
        )}

        {tab === "messaging" ? <MessagingPanel /> : tab === "whatsapp" ? <WhatsAppRoutes /> : tab === "homepage" ? <HomepagePanel /> : tab === "content" ? <ContentPanel /> : tab === "crm" ? <CrmPanel role={user.role} states={user.assigned_states || []} /> : tab === "staff" ? <StaffPanel /> : tab === "offers" ? <OffersPanel categories={categories} products={products} /> : tab === "customers" ? <CustomersPanel role={user.role} states={user.assigned_states || []} /> : tab === "collections" ? <CollectionsPanel products={products} onChanged={load} /> : tab === "categories" ? (
          <CategoriesPanel categories={categories} onChanged={load} />
        ) : tab === "products" ? (
          <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-products-table">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-[#12141A] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <th className="px-5 py-3.5">Blank</th>
                  <th className="px-5 py-3.5">Code</th>
                  <th className="px-5 py-3.5">Category</th>
                  <th className="px-5 py-3.5">Price</th>
                  <th className="px-5 py-3.5">Stock</th>
                  <th className="px-5 py-3.5">Live</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleProducts.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt={p.name} className="h-10 w-10 rounded-lg object-cover" />
                        <span className="font-medium text-slate-200">{p.name}</span>
                        {p.images?.length > 1 && (
                          <span data-testid={`admin-product-photos-${p.id}`} className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[10px] text-cyan-300">
                            {p.images.length} photos
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-400" data-testid={`admin-product-sku-${p.id}`}>{p.sku || "—"}</td>
                    <td className="px-5 py-3 font-mono text-xs text-fuchsia-300">{p.category}</td>
                    <td className="px-5 py-3 font-mono text-cyan-300">{formatINR(p.price)}</td>
                    <td className="px-5 py-3 font-mono text-slate-400">{p.stock}</td>
                    <td className="px-5 py-3">
                      <button
                        data-testid={`admin-toggle-live-${p.id}`}
                        onClick={() => toggleLive(p)}
                        title={p.is_live === false ? "Hidden — click to make live" : "Live — click to mark Coming soon"}
                        className={`flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                          p.is_live === false
                            ? "border-yellow-300/40 text-yellow-300 hover:bg-yellow-300/10"
                            : "border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${p.is_live === false ? "bg-yellow-300" : "bg-emerald-400"}`} />
                        {p.is_live === false ? "Coming soon" : "Live"}
                      </button>
                      <button
                        data-testid={`admin-toggle-new-launch-${p.id}`}
                        onClick={() => toggleNewLaunch(p)}
                        title={p.is_new_launch ? "In New Launch tab — click to remove" : "Click to add to the New Launch tab"}
                        className={`mt-1.5 flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                          p.is_new_launch
                            ? "border-yellow-300/50 bg-yellow-300/10 text-yellow-300"
                            : "border-white/10 text-slate-500 hover:border-yellow-300/40 hover:text-yellow-300"
                        }`}
                      >
                        <Sparkles size={10} /> {p.is_new_launch ? "New launch" : "Mark new"}
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`admin-edit-product-${p.id}`}
                          onClick={() => setEditing({ ...p, sku: p.sku ?? "", bulk_price: p.bulk_price ?? "", bulk_min: p.bulk_min ?? "", images: p.images?.length ? p.images : (p.image ? [p.image] : []), videos: p.videos || [], substrate: p.substrate.join(", ") })}
                          className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          data-testid={`admin-delete-product-${p.id}`}
                          onClick={() => deleteProduct(p.id)}
                          className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-red-400/40 hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-5 space-y-3" data-testid="admin-orders-list">
            {orders.length === 0 && (
              <div data-testid="admin-orders-empty" className="rounded-2xl border border-white/10 bg-[#12141A] p-12 text-center text-sm text-slate-500">
                No WhatsApp orders logged yet.
              </div>
            )}
            {orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-100">{o.customer_name} <span className="font-mono text-xs text-slate-500">/ {o.customer_phone}</span></div>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
                      {new Date(o.created_at).toLocaleString()}{o.state ? <span className="text-fuchsia-300" data-testid={`admin-order-state-${o.id}`}> · {o.state}</span> : null}
                      {o.coupon_code ? <span className="text-yellow-300" data-testid={`admin-order-coupon-${o.id}`}> · {o.coupon_code} −{formatINR(o.coupon_discount)}</span> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-yellow-300">{formatINR(o.total)}</span>
                    <span
                      data-testid={`admin-order-status-badge-${o.id}`}
                      className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${STAGE_COLORS[o.stage || "placed"] || ""}`}
                    >
                      {o.stage || "placed"}
                    </span>
                    {!isStaff && (
                      <button
                        data-testid={`admin-delete-order-${o.id}`}
                        onClick={() => deleteOrder(o.id)}
                        className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-red-400/40 hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 space-y-1 font-mono text-xs text-slate-400">
                  {o.items.map((i, idx) => (
                    <div key={`${i.product_id}-${idx}`} data-testid={`admin-order-item-${o.id}-${idx}`}>
                      • {i.name}
                      {i.sku && <span className="text-cyan-300"> [{i.sku}]</span>} x{i.qty} — {formatINR(i.price * i.qty)}
                      <ItemDetailsSummary item={i} prefix={`admin-order-item-${o.id}-${idx}`} />
                    </div>
                  ))}
                  {o.notes && <div className="pt-1 text-slate-500">Notes: {o.notes}</div>}
                </div>
                {o.address && (
                  <div className="mt-3 rounded-xl border border-white/10 bg-[#0A0B0E]/60 p-3" data-testid={`admin-order-address-${o.id}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-400">Deliver to</div>
                    <div className="mt-1 text-xs leading-relaxed text-slate-300">
                      {o.address}<br />
                      {o.city} — {o.pincode} · {o.state}
                      {o.gstin && <><br /><span className="font-mono text-cyan-300">GSTIN {o.gstin}</span></>}
                    </div>
                  </div>
                )}
                <OrderStageEditor
                  order={o}
                  onSaved={(updated) => setOrders((cur) => cur.map((x) => (x.id === updated.id ? updated : x)))}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      {backupOpen && <BackupModal onClose={() => setBackupOpen(false)} onImported={load} />}

      {changingPassword && (
        <ChangePasswordModal
          email={user?.email}
          isStaff={isStaff}
          onEmailChange={() => {}}
          onClose={() => setChangingPassword(false)}
        />
      )}

      {editing && (
        <ProductForm
          initial={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSave={saveProduct}
        />
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const { customer } = useCustomer();
  const ownerPhone = isAdminPhone(customer?.phone);
  const [staffAllowed, setStaffAllowed] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (ownerPhone) { setStaffAllowed(false); return; }
    const phone = customer?.phone;
    if (!phone) { setStaffAllowed(false); return; }
    api.get(`/staff/allowed/${phone}`)
      .then((res) => setStaffAllowed(res.data.allowed))
      .catch(() => setStaffAllowed(false));
  }, [customer?.phone, ownerPhone]);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  if (!ownerPhone && staffAllowed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0B0E] font-mono text-xs uppercase tracking-[0.25em] text-slate-500" data-testid="admin-checking-access">
        Checking access...
      </div>
    );
  }

  if (!ownerPhone && !staffAllowed) {
    return (
      <div className="relative flex min-h-screen flex-col items-center justify-center gap-4 overflow-hidden bg-[#0A0B0E] px-6 text-center" data-testid="admin-phone-blocked">
        <KeyRound size={22} className="text-fuchsia-400" />
        <h1 className="font-display text-2xl font-bold">Owner & staff access only</h1>
        <p className="max-w-md text-sm leading-relaxed text-slate-400">
          The dashboard opens for the owner numbers ({ADMIN_PHONES.join(" / ")}) and for staff numbers added by the owner. Enter the catalogue with your own number, then tap Admin.
        </p>
        <Link to="/" data-testid="admin-blocked-back-btn" className="mt-2 rounded-full border border-cyan-400/40 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-cyan-300 hover:bg-cyan-400/10">
          Back to catalogue
        </Link>
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0B0E] font-mono text-xs uppercase tracking-[0.25em] text-slate-500" data-testid="admin-loading">
        Verifying session...
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0B0E] px-4">
        <Link to="/" data-testid="login-back-to-store" className="absolute left-6 top-6 flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-slate-500 hover:text-cyan-300">
          <ArrowLeft size={13} /> Back to store
        </Link>
        <LoginForm onSuccess={setUser} />
      </div>
    );
  }

  return <Dashboard user={user} onLogout={logout} />;
}
